import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useMsal } from "@azure/msal-react";
import { supabase, dbToDeal, dealToDb, updateDeal, loadMessages, saveMessage, loadSaleComps, loadLeaseComps, saveSaleComp, saveLeaseComp, deleteSaleComp, deleteLeaseComp, uploadDocument, loadDocuments, deleteDocument, getDocumentUrl, extractTextFromFile, pdfToImages, getPdfPageImages, loadCompetition, loadUwVersions, loadDealNotes, saveDealNote } from "../../supabase";
import { STAGES, KILLED_STAGES, OUTREACH_STAGES, OUTREACH_PROMOTE_STAGE, isOutreachDeal } from "../../constants/dealStages";
import { outreachStagePatch, promotePatch } from "../../services/outreachFlow";
import OutreachPanel from "../outreach/OutreachPanel";
import { PRODUCT_TYPES } from "../../services/buyBoxEvaluator";
import BuyBoxOverride from "../common/BuyBoxOverride";
import { refreshDealTiming } from "../../services/dealTiming";
import { fmt } from "../../utils/formatters";
import { parseDealName } from "../../utils/parseDealName";
import { callClaude, callClaudeWithMessages } from "../../services/claude";
import { logActivity, autoIncrementTasks } from "../../services/activityLogger";
import PPIcon from "../common/PPIcon";
import iconWhite from "../../assets/logos/icon-white.png";
import StageBadge from "../common/StageBadge";
import { inputCls, Field } from "../common/FormHelpers";
import DealCompSet from "./DealCompSet";
import DealDashboard from "./DealDashboard";
import CompExtractionStaging from "./CompExtractionStaging";
import { nearestByType, drivingRoute } from "../../services/googleMaps";
import PropertyMedia from "./PropertyMedia";
import { ExternalLink } from "lucide-react";
import { memoPath } from "../../utils/memoLink";
// recharts and the memo's components are a large chunk; load them only when
// someone opens the IC Memo tab.
const IcMemoTab = lazy(() => import("../icmemo/IcMemoTab"));
const UwUploadDialog = lazy(() => import("../icmemo/UwUploadDialog"));

// Format a YYYY-MM-DD CFO date for read-mode display (e.g. "Jun 15, 2026").
// Uses UTC parsing so a Date-only column doesn't shift by timezone.
function fmtCfoDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// Format a created_at ISO timestamp + age in days (e.g. "Jun 2, 2026 · 14d ago").
function fmtDateAdded(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  const ageStr = days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
  return `${dateStr} · ${ageStr}`;
}

async function buildOutreachContext(deal) {
  let context = '';
  const searchAddr = deal.addresses?.[0]?.split(',')[0]?.trim() || '';
  if (!searchAddr) return context;
  const { data: matchingProps } = await supabase.from('properties').select('*').ilike('address', `%${searchAddr}%`);
  if (matchingProps && matchingProps.length > 0) {
    const prop = matchingProps[0];
    context += `Sourcing Property Match Found:\n`;
    context += `- Owner: ${prop.owner_name || 'Unknown'}\n`;
    context += `- RBA: ${prop.rba?.toLocaleString() || 'N/A'} SF\n`;
    if (prop.ceiling_ht) context += `- Clear Height: ${prop.ceiling_ht}\n`;
    if (prop.loading_docks) context += `- Loading Docks: ${prop.loading_docks}\n`;
    if (prop.drive_ins) context += `- Drive-Ins: ${prop.drive_ins}\n`;
    if (prop.percent_leased != null) context += `- Percent Leased: ${prop.percent_leased}%\n`;
    if (prop.construction_material) context += `- Construction: ${prop.construction_material}\n`;
    if (prop.last_sale_date) context += `- Last Sale: ${prop.last_sale_date} at $${prop.last_sale_price?.toLocaleString() || 'N/A'}\n`;
    const { data: outreach } = await supabase.from('outreach_log').select('*').eq('property_id', prop.id).order('sent_at', { ascending: false });
    if (outreach && outreach.length > 0) {
      context += `\nOutreach History (${outreach.length} total):\n`;
      for (const o of outreach) {
        context += `- ${new Date(o.sent_at).toLocaleDateString()}: LOI sent by ${o.sent_by} at $${o.loi_psf}/SF ($${o.loi_total?.toLocaleString()} total). Status: ${o.status}`;
        if (o.response) context += `. Response: ${o.response}`;
        context += `\n`;
      }
    }
    if (prop.owner_name) {
      const { data: sameOwner } = await supabase.from('properties').select('address, city, state, rba').ilike('owner_name', `%${prop.owner_name}%`).neq('id', prop.id).limit(10);
      if (sameOwner && sameOwner.length > 0) {
        context += `\nOther Properties by Same Owner (${prop.owner_name}):\n`;
        for (const p of sameOwner) { context += `- ${p.address}, ${p.city || ''} ${p.state || ''} — ${p.rba?.toLocaleString() || '?'} SF\n`; }
      }
    }
    const { data: propContacts } = await supabase.from('contacts').select('*').or(`property_id.eq.${prop.id}${prop.owner_name ? `,owner_name.ilike.%${prop.owner_name}%` : ''}`);
    if (propContacts && propContacts.length > 0) {
      context += `\nKnown Contacts:\n`;
      for (const c of propContacts) { context += `- ${c.name || 'Unknown'} (${c.contact_type || 'owner'}): ${c.email1 || 'no email'}${c.phone ? `, ${c.phone}` : ''}\n`; }
    }
  }
  return context;
}

function googleMapsUrl(addresses) {
  const arr = (Array.isArray(addresses) ? addresses : [addresses]).filter(Boolean);
  if (arr.length === 0) return "#";
  if (arr.length === 1) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(arr[0])}`;
  return `https://www.google.com/maps/dir/${arr.map(encodeURIComponent).join("/")}`;
}

function buildSystemPrompt(deal, saleComps = [], leaseComps = [], documents = [], outreachContext = '') {
  // Format a sale comp into a readable line for the prompt
  function fmtSaleComp(c) {
    const parts = [
      `Address: ${c.address}`,
      c.submarket   ? `Submarket: ${c.submarket}`                          : null,
      c.sf          ? `SF: ${fmt.sf(c.sf)}`                                : null,
      c.sale_price  ? `Sale Price: ${fmt.currency(c.sale_price)}`          : null,
      c.price_per_sf ? `Price/SF: $${Number(c.price_per_sf).toFixed(2)}`   : null,
      c.cap_rate    ? `Cap Rate: ${fmt.cap(c.cap_rate)}`                   : null,
      c.clear_height ? `Clear Height: ${c.clear_height}`                   : null,
      c.vintage     ? `Vintage: ${c.vintage}`                              : null,
      c.sale_date   ? `Sale Date: ${c.sale_date}`                          : null,
      c.notes       ? `Notes: ${c.notes}`                                  : null,
    ];
    return parts.filter(Boolean).join(" | ");
  }

  function fmtLeaseComp(c) {
    const parts = [
      `Address: ${c.address}`,
      c.submarket   ? `Submarket: ${c.submarket}`                                 : null,
      c.sf          ? `SF: ${fmt.sf(c.sf)}`                                       : null,
      c.tenant_size ? `Tenant Size: ${c.tenant_size}`                             : null,
      c.lease_rate  ? `Lease Rate NNN: $${Number(c.lease_rate).toFixed(2)}/SF`    : null,
      c.clear_height ? `Clear Height: ${c.clear_height}`                          : null,
      c.vintage     ? `Vintage: ${c.vintage}`                                     : null,
      c.lease_date  ? `Lease Date: ${c.lease_date}`                               : null,
      c.tenant_type ? `Tenant Type: ${c.tenant_type}`                             : null,
      c.notes       ? `Notes: ${c.notes}`                                         : null,
    ];
    return parts.filter(Boolean).join(" | ");
  }

  const saleCompsText = saleComps.length > 0
    ? saleComps.map((c, i) => `${i + 1}. ${fmtSaleComp(c)}`).join("\n")
    : "No sale comps entered yet.";

  const leaseCompsText = leaseComps.length > 0
    ? leaseComps.map((c, i) => `${i + 1}. ${fmtLeaseComp(c)}`).join("\n")
    : "No lease comps entered yet.";

  return `You are a sharp acquisitions analyst for PPUSA (Pan-Pacific USA), a private equity real estate firm focused on industrial assets. Help the team pressure-test assumptions, identify risks, run stress tests, evaluate thesis fit, and think through deal structure. Be direct and analytical. Ask clarifying questions when needed.

## PPUSA Investment Thesis
PPUSA targets small-bay infill industrial assets in high-barrier-to-entry sunbelt markets. The core thesis is the vacancy bifurcation story: large-format speculative development (200,000+ SF) is driving headline industrial vacancy rates up across major markets, creating a misleading signal. Meanwhile, small-bay infill product (5,000–40,000 SF multi-tenant) in established infill corridors is holding strong occupancy because:
- New supply is nearly impossible to build (land constraints, entitlement timelines, construction costs)
- Demand from last-mile distribution, local services, light manufacturing is sticky and growing
- Tenants cannot relocate easily — proximity to population density is the product
This bifurcation creates acquisition opportunities: sellers pricing off headline vacancy statistics are undervaluing infill small-bay assets.

## Target Markets
- **South Florida**: Doral, Medley, Opa-locka, Pompano Beach — MIA cargo corridor, port access, dense labor market
- **Tampa Bay**: Brandon, Pinellas Park, Oldsmar — MSA growth, constrained infill land
- **Dallas**: Garland, Mesquite, Irving, Grand Prairie — logistics hub, massive population growth driving last-mile demand

## Current Deal Under Review
${(() => {
    const addrs = deal.addresses ?? [];
    if (addrs.length <= 1) return `- **Address**: ${addrs[0] ?? "Unknown"}`;
    return `- **Addresses**:\n${addrs.map((a, i) => `  ${i + 1}. ${a}`).join("\n")}\n- **Portfolio**: Yes (${addrs.length} properties)`;
  })()}
- **Market**: ${deal.market}
- **Size**: ${fmt.sf(deal.squareFeet)}
- **Asking Price**: ${fmt.currency(deal.askingPrice)}
- **Cap Rate**: ${fmt.cap(deal.capRate)}
- **Deal Stage**: ${deal.stage}
- **OM / Source Notes**: ${deal.sourceNotes || "None."}
- **Investment Thesis**: ${deal.thesis || "Not yet documented."}
- **Open Questions**: ${deal.openQuestions || "None documented."}
- **Red Flags**: ${deal.redFlags || "None documented."}
- **PPUSA Underwritten Price**: ${deal.underwrittenPrice ? fmt.currencyOrDash(deal.underwrittenPrice) : "Not yet underwritten."}
- **Levered IRR**: ${deal.leveredIrr ? fmt.pct(deal.leveredIrr) : "Not yet underwritten."}
- **Equity Multiple**: ${deal.equityMultiple ? fmt.multiple(deal.equityMultiple) : "Not yet underwritten."}
- **Trended YoC**: ${deal.trendedYoc ? fmt.pct(deal.trendedYoc) : "Not yet underwritten."}
- **Untrended YoC**: ${deal.untrendedYoc ? fmt.pct(deal.untrendedYoc) : "Not yet underwritten."}
- **Average CoC**: ${deal.averageCoc ? fmt.pct(deal.averageCoc) : "Not yet underwritten."}

## Comparable Sales Data
${saleCompsText}

## Comparable Lease Data
${leaseCompsText}

## Comp Analysis Instructions
When the team asks about valuation, pricing, or thesis fit, you MUST reference the comp data above. Specifically:
- Compare the subject property's asking price and cap rate to the sale comp set
- Identify if the asking price is above, below, or in-line with comparable sales on a per-SF and cap rate basis
- Flag if the asking cap rate is aggressive (below comp average) or attractive (above comp average)
- Reference lease comps when discussing rent assumptions, tenant quality, and income stability
- If comp data is sparse, note that and suggest what additional comps would strengthen the analysis

${buildDocumentsSection(documents)}
${outreachContext ? `## Outreach & Sourcing History\n${outreachContext}\n` : ''}You have full context on this deal. When the team asks questions, ground your answers in the specifics above. Challenge weak assumptions, surface risks they may not have considered, and help them think rigorously about underwriting, structure, and exit.`;
}

// Builds the "Uploaded Documents" block injected into the system prompt.
// Caps at 5 most-recent docs and 3,000 chars per doc to stay within token limits.
function buildDocumentsSection(documents) {
  const MAX_DOCS  = 5;
  const MAX_CHARS = 3000;
  const total     = documents.length;
  // documents arrive newest-first from loadDocuments; take the first MAX_DOCS
  // Only include docs that have non-empty extracted text (null or "" means no extractable content)
  const toInclude = documents.slice(0, MAX_DOCS).filter((d) => d.extracted_text && d.extracted_text.trim().length > 0);

  if (toInclude.length === 0) {
    return "## Uploaded Documents\nNo documents with extractable text have been uploaded yet.\n";
  }

  const header = total > MAX_DOCS
    ? `## Uploaded Documents\n[Showing ${MAX_DOCS} most recent documents. ${total} total uploaded.]\n`
    : "## Uploaded Documents\n";

  const body = toInclude.map((doc) => {
    const text      = doc.extracted_text;
    const truncated = text.length > MAX_CHARS;
    const snippet   = truncated
      ? text.slice(0, MAX_CHARS) + "\n[...truncated — document continues beyond this point]"
      : text;
    return `### ${doc.file_name}\n${snippet}`;
  }).join("\n\n");

  return `${header}${body}

## Document Analysis Instructions
You have access to the full text of documents uploaded for this deal. When answering questions:
- Reference specific data points, figures, and details from the documents
- If an OM (Offering Memorandum) is uploaded, use its financials, tenant info, property details, and market data in your analysis
- Cross-reference document data with the deal facts and comp data for a complete picture
- If documents contain conflicting information vs. what's in the deal fields, flag the discrepancy
`;
}

// Fetch with retry + exponential backoff for rate limits (429)

// ─── OM extraction ────────────────────────────────────────────────────────────

// System prompt that instructs Claude to return ONLY a JSON object.
// The OM text is sent as the single user message.
function buildOMExtractionPrompt(marketList = []) {
  // Embed the exact market list so Claude can snap to a valid value.
  // Callers should pass the active MSA names from useMsaList — if empty we
  // just tell Claude to write the market as text and let the auto-tagger
  // fix it on save.
  const markets = (marketList || []).length > 0 ? marketList.join(", ") : "any US metro name";
  return `You are a data extraction assistant for a commercial real estate acquisitions team. Extract deal details from the Offering Memorandum. Return ONLY a valid JSON object — no markdown, no code fences, no explanation, just the raw JSON.

Required keys:
{
  "addresses": ["Full property address 1 including city, state, zip", "address 2 if portfolio deal", ...],
  "market": "Best match from: ${markets}",
  "squareFeet": <numeric total SF, number only, no commas>,
  "askingPrice": <numeric asking price in dollars, number only>,
  "capRate": <numeric cap rate as percentage, e.g. 5.80>,
  "sourceNotes": "Detailed summary extracted from the OM. Include: (1) Property description — building types, vintage, clear heights, total SF, number of buildings/units. (2) Tenant overview — number of tenants, top tenants with SF and lease expiration dates, WALT. (3) Financial highlights — in-place NOI, asking cap rate, price/SF, mark-to-market claims, projected NOI growth. (4) Investment thesis per the seller — value-add angle, rent growth story, replacement cost discount. (5) Market data — submarket vacancy, rent trends, supply pipeline. (6) Notable risks — tenant concentration, near-term expirations, capital needs. Be specific with numbers and data points from the OM."
}

Rules:
- "addresses" must always be an array, even for a single property (e.g. ["123 Main St, Tampa, FL 33601"])
- If the OM covers multiple properties, list all addresses in the array (up to 10)
- Use empty string "" for text fields you cannot determine with confidence
- Use 0 for numeric fields you cannot determine
- Only extract values you are confident about
- For market, pick the single best match from the list above; if none fit, use "Other"
- For sourceNotes, write a detailed structured summary covering all six categories above. Be specific with numbers, percentages, tenant names, and data points from the OM. Do not summarize vaguely — extract the actual figures.`;
}

// ─── Document helpers ─────────────────────────────────────────────────────────

// Returns a human-readable file size string (e.g. "2.4 MB", "340 KB")
function fmtFileSize(bytes) {
  if (!bytes) return "";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024)     return `${(bytes / 1_024).toFixed(0)} KB`;
  return `${bytes} B`;
}

// Pick an emoji icon based on the MIME type string
function fileIcon(fileType) {
  if (!fileType) return "📎";
  if (fileType.includes("pdf"))                       return "📄";
  if (fileType.includes("sheet") || fileType.includes("excel") || fileType.includes("csv")) return "📊";
  if (fileType.includes("word") || fileType.includes("document")) return "📝";
  return "📎";
}

const ACCEPTED_TYPES = ".pdf,.docx,.xlsx,.xls,.csv,.txt";

// Reusable drag-and-drop / click-to-upload zone.
// Props:
//   onFiles(fileList) — called with the selected FileList when files are chosen
//   uploading        — if true, shows a progress bar instead of the drop prompt
//   progress         — 0–100 number shown in the progress bar
function UploadZone({ onFiles, uploading, progress }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  }

  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors ${
        dragging
          ? "border-[#f04f44] bg-[rgba(233,53,41,0.1)]"
          : "border-[#4a4a4a] hover:border-[#5a5a5a] bg-[rgba(36,36,36,0.4)]"
      } ${uploading ? "cursor-default" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={(e) => e.target.files?.length && onFiles(e.target.files)}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-[#8a8a8a]">Uploading…</p>
          {/* Simple CSS progress bar */}
          <div className="w-full bg-[#3a3a3a] rounded-full h-1.5">
            <div
              className="bg-[#e93529] h-1.5 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#8a8a8a]">
          Drop files here or <span className="text-[#f04f44]">click to upload</span>
          <br />
          <span className="text-[#7a7a7a]">PDF, Word, Excel, CSV, TXT</span>
        </p>
      )}
    </div>
  );
}

// Documents section that lives in the Deal Room left panel.
// docs/onDocsChange are owned by DealRoom so the system prompt stays in sync.
function DocumentsSection({ dealId, docs, onDocsChange }) {
  const [open, setOpen] = useState(true); // start expanded so uploaded docs are immediately visible
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  async function handleFiles(fileList) {
    const files = Array.from(fileList);
    setUploading(true);
    setProgress(0);
    setUploadError(null);

    for (let i = 0; i < files.length; i++) {
      try {
        const row = await uploadDocument(dealId, files[i]);
        if (row) onDocsChange((prev) => [row, ...prev]);
      } catch (e) {
        console.error("Upload failed for", files[i].name, e);
        setUploadError(`Upload failed for "${files[i].name}": ${e.message}`);
      }
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setUploading(false);
    setProgress(0);
  }

  async function handleDelete(doc) {
    await deleteDocument(doc);
    onDocsChange((prev) => prev.filter((d) => d.id !== doc.id));
  }

  async function handleOpen(doc) {
    const url = await getDocumentUrl(doc.storage_path);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl overflow-hidden">
      {/* Collapsible header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[rgba(58,58,58,0.3)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Chevron open={open} />
          <span className="text-xs font-bold uppercase tracking-wider text-[#f04f44]">Documents</span>
          <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-1.5 py-0.5 font-medium">
            {docs.length}
          </span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          <UploadZone onFiles={handleFiles} uploading={uploading} progress={progress} />

          {uploadError && (
            <p className="text-xs text-[#f04f44] bg-[rgba(233,53,41,0.1)] border border-[rgba(233,53,41,0.2)] rounded px-2 py-1">{uploadError}</p>
          )}

          {docs.length === 0 && !uploading && (
            <p className="text-xs text-[#8a8a8a] italic">No documents uploaded yet.</p>
          )}

          {docs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-2 bg-[rgba(36,36,36,0.5)] border border-[rgba(58,58,58,0.4)] rounded-lg px-3 py-2 group"
            >
              <span className="text-base leading-none flex-shrink-0">{fileIcon(doc.file_type)}</span>
              {/* Clicking the name fetches a fresh signed URL and opens it */}
              <button
                onClick={() => handleOpen(doc)}
                className="flex-1 text-xs text-[#f4f4f4] hover:text-[#f04f44] text-left truncate transition-colors"
                title={doc.file_name}
              >
                {doc.file_name}
              </button>
              <span className="text-xs text-[#8a8a8a] flex-shrink-0">{fmtFileSize(doc.file_size)}</span>
              {/* Small indicator if text was extracted — useful to confirm AI can read it */}
              {doc.extracted_text && (
                <span className="text-xs text-[#44906a] flex-shrink-0" title="Text extracted — AI can read this document">✓</span>
              )}
              <button
                onClick={() => handleDelete(doc)}
                className="text-[#7a7a7a] hover:text-[#f04f44] text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1"
                title="Delete"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Comp Sections ────────────────────────────────────────────────────────────

// Empty form shapes — keys match DB column names exactly so we can spread them
// straight into the insert call without any extra mapping.
const EMPTY_SALE_COMP = {
  address: "", submarket: "", sf: "", sale_price: "", price_per_sf: "",
  cap_rate: "", clear_height: "", vintage: "", sale_date: "", notes: "",
};

const EMPTY_LEASE_COMP = {
  address: "", submarket: "", sf: "", tenant_size: "", lease_rate: "",
  clear_height: "", vintage: "", lease_date: "", tenant_type: "", notes: "",
};

// Chevron icon — points right when collapsed, down when expanded
function Chevron({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 text-[#8a8a8a] transition-transform ${open ? "rotate-90" : ""}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

// comps and onCompsChange are owned by DealRoom so the system prompt stays in sync
function SaleCompsSection({ dealId, comps, onCompsChange, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_SALE_COMP);
  const [saving, setSaving] = useState(false);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.address.trim()) return;
    setSaving(true);
    const payload = {
      address:      form.address.trim(),
      submarket:    form.submarket.trim() || null,
      sf:           form.sf ? Number(form.sf) : null,
      sale_price:   form.sale_price ? Number(form.sale_price) : null,
      price_per_sf: form.price_per_sf ? Number(form.price_per_sf) : null,
      cap_rate:     form.cap_rate ? Number(form.cap_rate) : 0,
      clear_height: form.clear_height.trim() || null,
      vintage:      form.vintage ? Number(form.vintage) : null,
      sale_date:    form.sale_date || null,
      notes:        form.notes.trim() || null,
    };
    const row = await saveSaleComp(dealId, payload);
    if (row) onCompsChange((prev) => [row, ...prev]);
    setForm(EMPTY_SALE_COMP);
    setShowForm(false);
    setSaving(false);
  }

  async function handleDelete(id) {
    await deleteSaleComp(id);
    onCompsChange((prev) => prev.filter((c) => c.id !== id));
  }

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl overflow-hidden">
      {/* Header row */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[rgba(58,58,58,0.3)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Chevron open={open} />
          <span className="text-xs font-bold uppercase tracking-wider text-[#f04f44]">Sale Comps</span>
          <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-1.5 py-0.5 font-medium">
            {comps.length}
          </span>
        </div>
        {/* + Add button — stop propagation so it doesn't toggle the section */}
        {!readOnly && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setOpen(true); setShowForm((s) => !s); }}
            onKeyDown={(e) => e.key === "Enter" && (e.stopPropagation(), setOpen(true), setShowForm((s) => !s))}
            className="text-xs text-[#f04f44] hover:text-[#f04f44] font-semibold transition-colors px-1"
          >
            + Add
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          {/* Inline add form */}
          {!readOnly && showForm && (
            <div className="bg-[rgba(36,36,36,0.6)] border border-[rgba(58,58,58,0.5)] rounded-lg p-3 flex flex-col gap-2">
              <p className="text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1">New Sale Comp</p>
              {/* Address — required */}
              <input placeholder="Address *" value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                className={inputCls(!form.address.trim() && saving)} />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Submarket" value={form.submarket}
                  onChange={(e) => setField("submarket", e.target.value)} className={inputCls()} />
                <input placeholder="SF" type="number" value={form.sf}
                  onChange={(e) => setField("sf", e.target.value)} className={inputCls()} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Sale Price ($)" type="number" value={form.sale_price}
                  onChange={(e) => setField("sale_price", e.target.value)} className={inputCls()} />
                <input placeholder="Price/SF ($)" type="number" value={form.price_per_sf}
                  onChange={(e) => setField("price_per_sf", e.target.value)} className={inputCls()} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="Cap Rate (%)" type="number" step="0.01" value={form.cap_rate}
                  onChange={(e) => setField("cap_rate", e.target.value)} className={inputCls()} />
                <input placeholder="Clear Ht." value={form.clear_height}
                  onChange={(e) => setField("clear_height", e.target.value)} className={inputCls()} />
                <input placeholder="Vintage" type="number" value={form.vintage}
                  onChange={(e) => setField("vintage", e.target.value)} className={inputCls()} />
              </div>
              <input placeholder="Sale Date" type="date" value={form.sale_date}
                onChange={(e) => setField("sale_date", e.target.value)} className={inputCls()} />
              <textarea placeholder="Notes" rows={2} value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                className={inputCls() + " resize-none"} />
              <div className="flex gap-2 mt-1">
                <button onClick={handleSave} disabled={saving || !form.address.trim()}
                  className="bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-40 text-[#333333] font-semibold text-xs px-3 py-1.5 rounded-md transition-colors">
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={() => { setShowForm(false); setForm(EMPTY_SALE_COMP); }}
                  className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#b4b4b4] text-xs px-3 py-1.5 rounded-md transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Comp cards */}
          {comps.length === 0 && !showForm && (
            <p className="text-xs text-[#8a8a8a] italic">No comps added yet.</p>
          )}
          {comps.map((c) => (
            <div key={c.id} className="bg-[rgba(36,36,36,0.5)] border border-[rgba(58,58,58,0.4)] rounded-lg px-3 py-2.5 relative group">
              {!readOnly && (
                <button
                  onClick={() => handleDelete(c.id)}
                  className="absolute top-2 right-2 text-[#7a7a7a] hover:text-[#f04f44] text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >×</button>
              )}
              <p className="text-xs font-semibold text-[#f4f4f4] pr-4 leading-snug">{c.address}</p>
              <p className="text-xs text-[#8a8a8a] mt-0.5">
                {[c.submarket, c.vintage, c.clear_height && `${c.clear_height} clear`].filter(Boolean).join(" · ")}
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                {c.sf        && <CompStat label="SF"       value={fmt.sf(c.sf)} />}
                {c.sale_price && <CompStat label="Price"   value={fmt.currency(c.sale_price)} />}
                {c.price_per_sf && <CompStat label="$/SF"  value={`$${Number(c.price_per_sf).toFixed(2)}`} />}
                {c.cap_rate  && <CompStat label="Cap"      value={fmt.cap(c.cap_rate)} accent />}
                {c.sale_date && <CompStat label="Date"     value={c.sale_date} />}
              </div>
              {c.notes && <p className="text-xs text-[#8a8a8a] mt-1.5 leading-relaxed">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeaseCompsSection({ dealId, comps, onCompsChange, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_LEASE_COMP);
  const [saving, setSaving] = useState(false);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.address.trim()) return;
    setSaving(true);
    const payload = {
      address:     form.address.trim(),
      submarket:   form.submarket.trim() || null,
      sf:          form.sf ? Number(form.sf) : null,
      tenant_size: form.tenant_size.trim() || null,
      lease_rate:  form.lease_rate ? Number(form.lease_rate) : null,
      clear_height: form.clear_height.trim() || null,
      vintage:     form.vintage ? Number(form.vintage) : null,
      lease_date:  form.lease_date || null,
      tenant_type: form.tenant_type.trim() || null,
      notes:       form.notes.trim() || null,
    };
    const row = await saveLeaseComp(dealId, payload);
    if (row) onCompsChange((prev) => [row, ...prev]);
    setForm(EMPTY_LEASE_COMP);
    setShowForm(false);
    setSaving(false);
  }

  async function handleDelete(id) {
    await deleteLeaseComp(id);
    onCompsChange((prev) => prev.filter((c) => c.id !== id));
  }

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[rgba(58,58,58,0.3)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Chevron open={open} />
          <span className="text-xs font-bold uppercase tracking-wider text-[#f04f44]">Lease Comps</span>
          <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-1.5 py-0.5 font-medium">
            {comps.length}
          </span>
        </div>
        {!readOnly && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setOpen(true); setShowForm((s) => !s); }}
            onKeyDown={(e) => e.key === "Enter" && (e.stopPropagation(), setOpen(true), setShowForm((s) => !s))}
            className="text-xs text-[#f04f44] hover:text-[#f04f44] font-semibold transition-colors px-1"
          >
            + Add
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          {!readOnly && showForm && (
            <div className="bg-[rgba(36,36,36,0.6)] border border-[rgba(58,58,58,0.5)] rounded-lg p-3 flex flex-col gap-2">
              <p className="text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1">New Lease Comp</p>
              <input placeholder="Address *" value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                className={inputCls(!form.address.trim() && saving)} />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Submarket" value={form.submarket}
                  onChange={(e) => setField("submarket", e.target.value)} className={inputCls()} />
                <input placeholder="SF" type="number" value={form.sf}
                  onChange={(e) => setField("sf", e.target.value)} className={inputCls()} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Tenant Size" value={form.tenant_size}
                  onChange={(e) => setField("tenant_size", e.target.value)} className={inputCls()} />
                <input placeholder="Lease Rate NNN ($/SF)" type="number" step="0.01" value={form.lease_rate}
                  onChange={(e) => setField("lease_rate", e.target.value)} className={inputCls()} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="Clear Ht." value={form.clear_height}
                  onChange={(e) => setField("clear_height", e.target.value)} className={inputCls()} />
                <input placeholder="Vintage" type="number" value={form.vintage}
                  onChange={(e) => setField("vintage", e.target.value)} className={inputCls()} />
                <input placeholder="Tenant Type" value={form.tenant_type}
                  onChange={(e) => setField("tenant_type", e.target.value)} className={inputCls()} />
              </div>
              <input placeholder="Lease Date" type="date" value={form.lease_date}
                onChange={(e) => setField("lease_date", e.target.value)} className={inputCls()} />
              <textarea placeholder="Notes" rows={2} value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                className={inputCls() + " resize-none"} />
              <div className="flex gap-2 mt-1">
                <button onClick={handleSave} disabled={saving || !form.address.trim()}
                  className="bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-40 text-[#333333] font-semibold text-xs px-3 py-1.5 rounded-md transition-colors">
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={() => { setShowForm(false); setForm(EMPTY_LEASE_COMP); }}
                  className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#b4b4b4] text-xs px-3 py-1.5 rounded-md transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {comps.length === 0 && !showForm && (
            <p className="text-xs text-[#8a8a8a] italic">No comps added yet.</p>
          )}
          {comps.map((c) => (
            <div key={c.id} className="bg-[rgba(36,36,36,0.5)] border border-[rgba(58,58,58,0.4)] rounded-lg px-3 py-2.5 relative group">
              {!readOnly && (
                <button
                  onClick={() => handleDelete(c.id)}
                  className="absolute top-2 right-2 text-[#7a7a7a] hover:text-[#f04f44] text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >×</button>
              )}
              <p className="text-xs font-semibold text-[#f4f4f4] pr-4 leading-snug">{c.address}</p>
              <p className="text-xs text-[#8a8a8a] mt-0.5">
                {[c.submarket, c.tenant_type, c.vintage, c.clear_height && `${c.clear_height} clear`].filter(Boolean).join(" · ")}
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                {c.sf          && <CompStat label="SF"          value={fmt.sf(c.sf)} />}
                {c.tenant_size && <CompStat label="Tenant SF"   value={c.tenant_size} />}
                {c.lease_rate  && <CompStat label="Rate NNN"    value={`$${Number(c.lease_rate).toFixed(2)}/SF`} accent />}
                {c.lease_date  && <CompStat label="Date"        value={c.lease_date} />}
              </div>
              {c.notes && <p className="text-xs text-[#8a8a8a] mt-1.5 leading-relaxed">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Small inline key/value stat used inside comp cards
function CompStat({ label, value, accent }) {
  return (
    <span className="text-xs">
      <span className="text-[#8a8a8a]">{label} </span>
      <span className={accent ? "text-[#f04f44] font-semibold" : "text-[#b4b4b4] font-medium"}>{value}</span>
    </span>
  );
}

// ─── Nearest Infrastructure ───────────────────────────────────────────────
// Given a deal with lat/lng, finds the closest airport + seaport via Places
// Nearby Search, then queries Directions for drive time to each. Results
// are cached in localStorage (keyed by deal id) so we don't re-query on
// every navigate. Click "Refresh" to force recompute.
// ─── KillDetailSection ──────────────────────────────────────────────────────
// Rendered on the deal page for terminal deals (Passed / Lost / Dead). Shows
// killed_reason + killed_narrative from the deal row itself. For Lost deals,
// also lazy-loads the deal_competition row and shows the bid stack + delta +
// broker debrief. onRegrade re-opens the Kill modal so the user can edit
// what's already there.
function KillDetailSection({ deal, onRegrade }) {
  // Competition detail was Lost-only in the initial cut, but Passed and Dead
  // deals can still have "so-and-so ended up buying it at $X" context worth
  // capturing. Load unconditionally now; the section renders as "No
  // competition detail recorded" when the row is absent.
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!deal.id) return;
    let cancelled = false;
    setLoading(true);
    loadCompetition(deal.id).then(row => {
      if (cancelled) return;
      setComp(row);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [deal.id, deal.stage]);

  const killedDate = deal.killedAt ? new Date(deal.killedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const fmtMoney = (n) => (n == null ? "—" : n >= 1_000_000 ? `$${(n/1_000_000).toFixed(2)}M` : `$${Number(n).toLocaleString()}`);
  const fmtPsf   = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}/SF`);
  const fmtPct   = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] rounded-xl p-5 mt-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-bold text-[#f4f4f4] font-montserrat uppercase tracking-wider">
          Why we {deal.stage === "Passed" ? "passed" : deal.stage === "Lost" ? "lost" : "killed this"}
        </h3>
        <button onClick={onRegrade} className="text-[11px] text-[#f04f44] hover:text-[#f04f44] font-semibold">
          Edit ✎
        </button>
      </div>

      <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
        <span className="text-xs text-[#8a8a8a] uppercase tracking-wider">Reason</span>
        <span className="text-[#f4f4f4]">{deal.killedReason || <span className="text-[#8a8a8a]">—</span>}</span>
        {killedDate && (<>
          <span className="text-xs text-[#8a8a8a] uppercase tracking-wider">Date</span>
          <span className="text-[#b4b4b4]">{killedDate}</span>
        </>)}
        {deal.killedNarrative && (<>
          <span className="text-xs text-[#8a8a8a] uppercase tracking-wider">Narrative</span>
          <span className="text-[#f4f4f4] whitespace-pre-wrap">{deal.killedNarrative}</span>
        </>)}
      </div>

      {(loading || comp) && (
        <div className="mt-4 pt-4 border-t border-[rgba(58,58,58,0.4)]">
          <h4 className="text-xs font-bold text-[#8a8a8a] uppercase tracking-wider mb-2">Competition Detail</h4>
          {loading ? (
            <p className="text-xs text-[#8a8a8a]">Loading…</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Bidders</span>
                <span className="text-[#f4f4f4]">{comp.num_bidders_estimated ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Best & Final</span>
                <span className="text-[#f4f4f4]">{comp.made_best_and_final === true ? "Yes" : comp.made_best_and_final === false ? "No" : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Our Bid</span>
                <span className="text-[#f4f4f4]">{fmtPsf(comp.our_final_bid_psf)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Our Bid Total</span>
                <span className="text-[#f4f4f4]">{fmtMoney(comp.our_final_bid_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Winning Bid</span>
                <span className="text-[#f04f44] font-semibold">{fmtPsf(comp.winning_bid_psf)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Winning Total</span>
                <span className="text-[#f04f44] font-semibold">{fmtMoney(comp.winning_bid_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Δ to Winner</span>
                <span className="text-[#f4f4f4]">{fmtPsf(comp.delta_to_winner_psf)} ({fmtPct(comp.delta_pct)})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8a8a]">Winner</span>
                <span className="text-[#f4f4f4] truncate">{comp.winner_name || "—"}</span>
              </div>
              {comp.broker_debrief_notes && (
                <div className="col-span-2 pt-2 border-t border-[rgba(58,58,58,0.3)] mt-1">
                  <p className="text-xs text-[#8a8a8a] uppercase tracking-wider mb-1">Broker Debrief</p>
                  <p className="text-[#f4f4f4] whitespace-pre-wrap">{comp.broker_debrief_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NearestInfrastructure({ deal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const cacheKey = `pp:nearest:${deal?.id || ""}`;

  useEffect(() => {
    if (!deal?.id) return;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) setData(JSON.parse(raw));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id]);

  async function compute() {
    if (!deal?.latitude || !deal?.longitude) { setErr("Deal has no coordinates yet."); return; }
    setLoading(true); setErr(null);
    try {
      const origin = { lat: Number(deal.latitude), lng: Number(deal.longitude) };
      const [airport, seaport] = await Promise.all([
        nearestByType(origin, "airport"),
        nearestByType(origin, "port"),
      ]);
      const [airportRoute, seaportRoute] = await Promise.all([
        airport ? drivingRoute(origin, { lat: airport.lat, lng: airport.lng }) : null,
        seaport ? drivingRoute(origin, { lat: seaport.lat, lng: seaport.lng }) : null,
      ]);
      const payload = {
        airport: airport ? { ...airport, route: airportRoute } : null,
        seaport: seaport ? { ...seaport, route: seaportRoute } : null,
        computedAt: new Date().toISOString(),
      };
      setData(payload);
      try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch {}
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  if (!deal?.latitude || !deal?.longitude) return null;

  return (
    <div className="mt-3 pt-3 border-t border-[rgba(58,58,58,0.4)]">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a]">Nearest Infrastructure</span>
        <button
          onClick={compute}
          disabled={loading}
          className="text-[10px] font-semibold text-[#f04f44] hover:text-[#f04f44] disabled:opacity-50"
          title={data ? "Recompute drive times" : "Compute drive times via Google"}
        >
          {loading ? "…" : data ? "Refresh" : "Compute"}
        </button>
      </div>
      {err && <div className="text-[10px] text-[#f04f44] mb-1">{err}</div>}
      {!data && !loading && !err && <div className="text-[10px] text-[#8a8a8a]">Click Compute to fetch nearest airport + seaport drive times.</div>}
      {data && (
        <div className="space-y-1 text-[11px]">
          {["airport", "seaport"].map(kind => {
            const it = data[kind];
            const label = kind === "airport" ? "✈ Airport" : "⚓ Seaport";
            if (!it) return (
              <div key={kind} className="flex items-center justify-between text-[#8a8a8a]">
                <span>{label}</span><span>—</span>
              </div>
            );
            return (
              <div key={kind} className="flex items-center justify-between">
                <div className="min-w-0 flex-1 mr-2">
                  <div className="text-[#f4f4f4] font-medium truncate">{label}: {it.name}</div>
                  {it.address && <div className="text-[#8a8a8a] text-[10px] truncate">{it.address}</div>}
                </div>
                <div className="text-right shrink-0">
                  {it.route?.durationText
                    ? <div className="text-[#f4f4f4] font-semibold">{it.route.durationText}</div>
                    : <div className="text-[#8a8a8a]">—</div>}
                  {it.route?.distanceText && <div className="text-[#8a8a8a] text-[10px]">{it.route.distanceText}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Deal Room ────────────────────────────────────────────────────────────────

function DealRoom({ deal, allDeals = [], darkMode, onBack, onDealUpdated, onDealDeleted, onShowOnMap, onOpenKillModal, readOnly = false }) {
  const WELCOME = {
    id: "welcome",
    role: "assistant",
    text: "I know this deal. Ask me anything — thesis fit, red flags, comp analysis, stress tests.",
  };

  // Local copy of the deal — updated on edit/stage change without re-mounting.
  // Also re-syncs when the parent's `deal` prop changes so writes that happen
  // at the App level (Kill modal, background refreshes) reflect on-screen
  // without needing to leave + return to the deal room.
  const [localDeal, setLocalDeal] = useState(deal);
  useEffect(() => { setLocalDeal(deal); }, [deal]);

  // Every underwriting this deal has had. The deal's own columns mirror the
  // CURRENT one, so an empty list (or a database without migration 028) simply
  // means no pill and the deal reads exactly as it did before.
  //
  // These two live HERE, next to the effect that fills them and the derived
  // value that reads them, rather than down with the rest of the state. Declared
  // below their first use they are in the temporal dead zone, and `shownUw`
  // throws "Cannot access 'uwVersions' before initialization" on first render -
  // which takes the whole deal room down to a black screen for every deal.
  const [uwVersions, setUwVersions] = useState([]);
  const [uwVersionId, setUwVersionId] = useState(null);
  const [uwUploadOpen, setUwUploadOpen] = useState(false);
  // The IC Memo waits for this: opened before the versions arrive it would
  // first load the deal's draft memo and then the real one.
  const [uwLoaded, setUwLoaded] = useState(false);

  // What people decided about this deal. Loaded beside the versions and, like
  // them, declared HERE rather than with the other state - `shownUw` and the
  // render read these in the component body, and a const below its first use is
  // in the temporal dead zone.
  const [dealNotes, setDealNotes] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!deal?.id) { setDealNotes([]); return; }
    loadDealNotes(deal.id).then((n) => { if (alive) setDealNotes(n); });
    return () => { alive = false; };
  }, [deal?.id]);

  // Pull the underwriting history and default to whichever version is current -
  // that is the one the deal's own columns already mirror, so the page opens
  // showing exactly what it showed before the pill existed.
  useEffect(() => {
    let alive = true;
    setUwLoaded(false);
    if (!deal?.id) { setUwVersions([]); setUwVersionId(null); return; }
    loadUwVersions(deal.id).then((vs) => {
      if (!alive) return;
      setUwVersions(vs);
      setUwLoaded(true);
      const cur = vs.find((v) => v.is_current) || vs[vs.length - 1];
      setUwVersionId(cur ? cur.id : null);
    });
    return () => { alive = false; };
  }, [deal?.id]);

  // A version recorded from the upload dialog: reload the versions, show the
  // new one, and re-read the deal row, which now mirrors it if it is current.
  const afterUwUpload = async (v) => {
    setUwUploadOpen(false);
    const vs = await loadUwVersions(deal.id);
    setUwVersions(vs);
    if (v?.id) setUwVersionId(v.id);
    const { data } = await supabase.from("deals").select("*").eq("id", deal.id).single();
    if (data) {
      const updated = { ...dbToDeal(data), photoPath: localDeal.photoPath };
      setLocalDeal(updated);
      onDealUpdated?.(updated);
    }
  };

  // What the underwriting rows below actually read. With no versions loaded this
  // is the deal itself, unchanged; with a version selected it is that version.
  // Falling back per-field rather than wholesale means a version that does not
  // carry, say, average_coc still shows the deal's, instead of blanking it.
  const shownUw = (() => {
    const v = uwVersions.find((x) => x.id === uwVersionId);
    if (!v) return { ...localDeal, _label: null, _isCurrent: true };
    const pick = (a, b) => (a == null ? b : a);
    return {
      ...localDeal,
      underwrittenPrice: pick(v.purchase_price, localDeal.underwrittenPrice),
      leveredIrr:        pick(v.levered_irr, localDeal.leveredIrr),
      equityMultiple:    pick(v.equity_multiple, localDeal.equityMultiple),
      trendedYoc:        pick(v.trended_yoc, localDeal.trendedYoc),
      untrendedYoc:      pick(v.untrended_yoc, localDeal.untrendedYoc),
      averageCoc:        pick(v.average_coc, localDeal.averageCoc),
      capRate:           pick(v.cap_rate, localDeal.capRate),
      opexPsf:           pick(v.opex_psf, localDeal.opexPsf),
      _label: v.label || `V${v.version}`,
      _isCurrent: !!v.is_current,
      _v: v,
    };
  })();

  // Display messages (the welcome message is always prepended; it is never persisted)
  const [messages, setMessages] = useState([WELCOME]);
  // API history mirrors messages but excludes the welcome message (Claude never sees it)
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // True while we're fetching prior messages from Supabase on mount
  const [historyLoading, setHistoryLoading] = useState(true);

  // Buy-box manual override — set while the toggle is round-tripping so the
  // Override chevron greys out and duplicate clicks are ignored.
  const [buyBoxSaving, setBuyBoxSaving] = useState(false);

  async function handleBuyBoxOverride(payload) {
    if (buyBoxSaving) return;
    setBuyBoxSaving(true);
    // updateDeal reads buyBoxSource from the deal object. Send just the
    // buy-box columns — no other deal fields — so we don't inadvertently
    // trigger address re-geocoding or MSA re-tagging on a verdict toggle.
    const { data, error } = await updateDeal(localDeal.id, {
      addresses: localDeal.addresses,       // preserved so dealToDb JSON-encodes correctly
      market: localDeal.market,
      squareFeet: localDeal.squareFeet,
      askingPrice: localDeal.askingPrice,
      capRate: localDeal.capRate,
      stage: localDeal.stage,
      sourceNotes: localDeal.sourceNotes,
      thesis: localDeal.thesis,
      openQuestions: localDeal.openQuestions,
      redFlags: localDeal.redFlags,
      underwrittenPrice: localDeal.underwrittenPrice,
      leveredIrr: localDeal.leveredIrr,
      equityMultiple: localDeal.equityMultiple,
      trendedYoc: localDeal.trendedYoc,
      untrendedYoc: localDeal.untrendedYoc,
      averageCoc: localDeal.averageCoc,
      marketed: localDeal.marketed,
      callForOffersDate: localDeal.callForOffersDate,
      productType: localDeal.productType,
      yearBuilt: localDeal.yearBuilt,
      clearHeight: localDeal.clearHeight,
      // The override payload — either forces a manual verdict or resets to auto
      // so updateDeal's evaluator branch re-grades cleanly.
      buyBoxSource: payload.buyBoxSource,
      buyBoxVerdict: payload.buyBoxVerdict,
      buyBoxReason: payload.buyBoxReason,
    });
    setBuyBoxSaving(false);
    if (error) {
      window.alert(`Couldn't save override: ${error.message}`);
      return;
    }
    const updated = { ...dbToDeal(data), photoPath: localDeal.photoPath };
    setLocalDeal(updated);
    onDealUpdated?.(updated);
  }

  // ── Edit / delete / stage state ──
  const [editing,       setEditing]       = useState(false);
  const [editForm,      setEditForm]      = useState(null);
  const [editSaving,    setEditSaving]    = useState(false);
  const [editError,     setEditError]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const dealPhotoInputRef = useRef(null);

  // The signed-in user, for attributing activity-log entries and notes.
  const { accounts: trackAccounts } = useMsal();
  const trackUser = trackAccounts?.[0]?.name || trackAccounts?.[0]?.username || null;

  // Comp arrays — owned here so we can pass them to child sections AND keep the
  // system prompt in sync whenever they change.
  const [saleComps, setSaleComps]   = useState([]);
  const [leaseComps, setLeaseComps] = useState([]);
  // Documents array — also owned here so the system prompt stays current when
  // files are added or deleted via the DocumentsSection below.
  const [documents, setDocuments]   = useState([]);
  // Signed URL for the property photo (generated once on mount, 1-hr expiry)
  const [photoUrl, setPhotoUrl]     = useState(null);

  // Outreach context from the Database tab — enriches the AI system prompt
  const [outreachCtx, setOutreachCtx] = useState('');
  // Outreach entries linked to this deal (for display in sidebar)
  const [dealOutreach, setDealOutreach] = useState([]);

  // ── Comp extraction state (shared between DealCompSet and the per-doc
  //    Extract button in the Documents section so both entry points open
  //    the same staging modal) ──
  const [extractRequest, setExtractRequest] = useState(null); // { file, fileName } | null
  const [extractingDocId, setExtractingDocId] = useState(null); // for in-row spinner
  const [compSetReloadKey, setCompSetReloadKey] = useState(0);

  async function triggerExtractFromDoc(doc) {
    if (!doc) return;
    setExtractingDocId(doc.id);
    try {
      const url = await getDocumentUrl(doc.storage_path);
      const resp = await fetch(url);
      const blob = await resp.blob();
      const file = new File([blob], doc.file_name, { type: blob.type || doc.file_type });
      setExtractRequest({ file, fileName: doc.file_name });
    } catch (e) {
      console.error("Failed to fetch document for extraction:", e);
      window.alert(`Failed to fetch document for extraction: ${e.message || e}`);
    } finally {
      setExtractingDocId(null);
    }
  }

  // systemPrompt is a ref so sendMessage always reads the latest value without
  // needing it in its dependency array. We update it via the effect below.
  const systemPrompt = useRef(buildSystemPrompt(localDeal, [], [], [], ''));
  // Cache rendered PDF images so we don't re-download/re-render on every message
  const pdfImageCache = useRef(null);

  const bottomRef = useRef(null);

  // On mount: load chat history, comps, and documents all in parallel
  useEffect(() => {
    async function fetchAll() {
      try {
        const [msgRows, saleRows, leaseRows, docRows, signedPhotoUrl] = await Promise.all([
          loadMessages(deal.id),
          loadSaleComps(deal.id),
          loadLeaseComps(deal.id),
          loadDocuments(deal.id),
          // Same precedence as the pipeline: the chosen photo, else the
          // Street View frame captured for the coordinate.
          (deal.photoPath || deal.streetViewPath)
            ? getDocumentUrl(deal.photoPath || deal.streetViewPath)
            : Promise.resolve(null),
        ]);

        if (msgRows.length > 0) {
          setMessages([
            WELCOME,
            ...msgRows.map((r, i) => ({ id: i + 1, role: r.role, text: r.content })),
          ]);
          setHistory(msgRows.map((r) => ({ role: r.role, content: r.content })));
        }

        setSaleComps(saleRows);
        setLeaseComps(leaseRows);
        setDocuments(docRows);
        console.log("Loaded documents for deal:", deal.id, docRows);
        if (signedPhotoUrl) setPhotoUrl(signedPhotoUrl);

        // Load outreach context from the database (non-blocking)
        buildOutreachContext(deal).then(ctx => setOutreachCtx(ctx)).catch(() => {});

        // Load outreach entries linked to this deal
        supabase.from('outreach_log').select('*').eq('deal_id', deal.id).order('sent_at', { ascending: false })
          .then(({ data }) => { if (data) setDealOutreach(data); });
      } catch (err) {
        console.error("fetchAll error:", err.message);
      } finally {
        setHistoryLoading(false);
      }
    }
    fetchAll();
  }, [deal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rebuild the system prompt whenever the deal, comps, documents, or outreach context change.
  useEffect(() => {
    systemPrompt.current = buildSystemPrompt(localDeal, saleComps, leaseComps, documents, outreachCtx);
  }, [localDeal, saleComps, leaseComps, documents, outreachCtx]);

  // Clear the PDF image cache when switching deals so stale images aren't reused
  useEffect(() => {
    pdfImageCache.current = null;
  }, [deal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    const userMsg = { id: messages.length, role: "user", text };
    // history state always stores plain text so it stays lightweight
    const newHistory = [...history, { role: "user", content: text }];

    setMessages((prev) => [...prev, userMsg]);
    setHistory(newHistory);
    setLoading(true);

    // Persist the user message — fire and forget
    saveMessage(deal.id, "user", text);

    try {
      console.log("Building system prompt with", documents.length, "documents");

      // Sliding window: keep only the last 20 messages to prevent unbounded token growth
      // Document text is already included in the system prompt via buildDocumentsSection
      const MAX_HISTORY = 20;
      const apiMessages = newHistory.length > MAX_HISTORY
        ? newHistory.slice(-MAX_HISTORY)
        : newHistory;

      const reply = await callClaudeWithMessages(systemPrompt.current, apiMessages, 2048);
      setMessages((prev) => [
        ...prev,
        { id: prev.length, role: "assistant", text: reply },
      ]);
      setHistory((prev) => [...prev, { role: "assistant", content: reply }]);
      // Persist the assistant reply — fire and forget
      saveMessage(deal.id, "assistant", reply);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { id: prev.length, role: "error", text: err.message },
      ]);
    } finally {
      setLoading(false);
    }
  }

  // ── Edit helpers ────────────────────────────────────────────────────────────

  function startEdit() {
    const existingName = parseDealName(localDeal.sourceNotes) || "";
    setEditForm({
      dealName:      existingName,
      addresses:     [...localDeal.addresses],
      market:        localDeal.market,
      squareFeet:    localDeal.squareFeet  ? String(localDeal.squareFeet)  : "",
      askingPrice:   localDeal.askingPrice ? String(localDeal.askingPrice) : "",
      capRate:       localDeal.capRate     ? String(localDeal.capRate)     : "",
      stage:         localDeal.stage,
      sourceNotes:   localDeal.sourceNotes   ?? "",
      thesis:        localDeal.thesis        ?? "",
      openQuestions: localDeal.openQuestions ?? "",
      redFlags:      localDeal.redFlags      ?? "",
      underwrittenPrice: localDeal.underwrittenPrice ? String(localDeal.underwrittenPrice) : "",
      leveredIrr:        localDeal.leveredIrr        ? String(localDeal.leveredIrr)        : "",
      equityMultiple:    localDeal.equityMultiple    ? String(localDeal.equityMultiple)    : "",
      trendedYoc:        localDeal.trendedYoc        ? String(localDeal.trendedYoc)        : "",
      untrendedYoc:      localDeal.untrendedYoc      ? String(localDeal.untrendedYoc)      : "",
      averageCoc:        localDeal.averageCoc        ? String(localDeal.averageCoc)        : "",
      callForOffersDate: localDeal.callForOffersDate || "",
      marketed:          !!localDeal.marketed,
      productType:       localDeal.productType || "",
      yearBuilt:         localDeal.yearBuilt   ? String(localDeal.yearBuilt)   : "",
      clearHeight:       localDeal.clearHeight ? String(localDeal.clearHeight) : "",
      opexPsf:           localDeal.opexPsf != null ? String(localDeal.opexPsf) : "",
      brokerName:        localDeal.brokerName  || "",
      brokerFirm:        localDeal.brokerFirm  || "",
      brokerEmail:       localDeal.brokerEmail || "",
      madeBestAndFinal:  !!localDeal.madeBestAndFinal,
    });
    setEditing(true);
    setEditError(null);
  }

  function cancelEdit() {
    setEditing(false);
    setEditForm(null);
    setEditError(null);
  }

  function setEditField(field, value) {
    setEditForm((f) => ({ ...f, [field]: value }));
  }

  function setEditAddr(idx, value) {
    setEditForm((f) => {
      const arr = [...f.addresses];
      arr[idx] = value;
      return { ...f, addresses: arr };
    });
  }

  function addEditAddr() {
    setEditForm((f) =>
      f.addresses.length < 10 ? { ...f, addresses: [...f.addresses, ""] } : f
    );
  }

  function removeEditAddr(idx) {
    setEditForm((f) => ({ ...f, addresses: f.addresses.filter((_, i) => i !== idx) }));
  }

  async function saveEdit() {
    if (!editForm.addresses[0]?.trim()) { setEditError("At least one address is required."); return; }
    setEditSaving(true);
    setEditError(null);

    // Update deal name in source_notes (first line "Name: ...")
    let finalNotes = editForm.sourceNotes.trim();
    const existingNameMatch = finalNotes.match(/^Name:\s*(.+)$/m);
    if (editForm.dealName.trim()) {
      if (existingNameMatch) {
        finalNotes = finalNotes.replace(/^Name:\s*(.+)$/m, "Name: " + editForm.dealName.trim());
      } else {
        finalNotes = "Name: " + editForm.dealName.trim() + (finalNotes ? "\n" + finalNotes : "");
      }
    } else if (existingNameMatch) {
      finalNotes = finalNotes.replace(/^Name:\s*(.+)\n?/m, "");
    }

    const payload = {
      addresses:     editForm.addresses.filter((a) => a.trim()),
      // market intentionally omitted — updateDeal → _tagAddresses re-derives
      // it from the address list every save.
      squareFeet:    editForm.squareFeet    ? Number(editForm.squareFeet)    : 0,
      askingPrice:   editForm.askingPrice   ? Number(editForm.askingPrice)   : null,
      capRate:       editForm.capRate       ? Number(editForm.capRate)       : 0,
      stage:         editForm.stage,
      sourceNotes:   finalNotes,
      thesis:        editForm.thesis.trim(),
      openQuestions: editForm.openQuestions.trim(),
      redFlags:      editForm.redFlags.trim(),
      photoPath:     localDeal.photoPath, // preserve photo
      underwrittenPrice: editForm.underwrittenPrice ? Number(editForm.underwrittenPrice) : null,
      leveredIrr:        editForm.leveredIrr        ? Number(editForm.leveredIrr)        : null,
      equityMultiple:    editForm.equityMultiple    ? Number(editForm.equityMultiple)    : null,
      trendedYoc:        editForm.trendedYoc        ? Number(editForm.trendedYoc)        : null,
      untrendedYoc:      editForm.untrendedYoc      ? Number(editForm.untrendedYoc)      : null,
      averageCoc:        editForm.averageCoc        ? Number(editForm.averageCoc)        : null,
      opexPsf:           editForm.opexPsf === "" || editForm.opexPsf == null ? null : Number(editForm.opexPsf),
      callForOffersDate: editForm.callForOffersDate || null,
      marketed:          !!editForm.marketed,
      productType:       editForm.productType || null,
      yearBuilt:         editForm.yearBuilt   ? Number(editForm.yearBuilt)   : null,
      clearHeight:       editForm.clearHeight ? Number(editForm.clearHeight) : null,
      brokerName:        editForm.brokerName?.trim()  || null,
      brokerFirm:        editForm.brokerFirm?.trim()  || null,
      brokerEmail:       editForm.brokerEmail?.trim() || null,
      madeBestAndFinal:  !!editForm.madeBestAndFinal,
    };

    const { data, error } = await updateDeal(localDeal.id, payload);
    if (error) {
      setEditError(error.message);
    } else {
      const updated = { ...dbToDeal(data), photoPath: localDeal.photoPath };
      // If the edit changed the stage, log it so the weekly stage-entry
      // counts on the dashboard reflect the move. (The same logging happens
      // in App.handleStageChange for grid-level moves; this catches edits
      // made from inside the deal room.)
      if (localDeal.stage !== updated.stage) {
        const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
        const account = trackAccounts?.[0];
        await logActivity({
          userName: account?.name || trackUser || "Unknown",
          userEmail: account?.username || "",
          action: "stage_changed",
          description: `${dealName} moved from ${localDeal.stage || "?"} → ${updated.stage}`,
          entityType: "deal",
          entityId: localDeal.id,
          metadata: { old_stage: localDeal.stage, new_stage: updated.stage, deal_name: dealName },
        });
        await autoIncrementTasks(account?.username, "stage_changed");
        refreshDealTiming(updated);
      }
      setLocalDeal(updated);
      setEditing(false);
      setEditForm(null);
      onDealUpdated?.(updated);
    }
    setEditSaving(false);
  }

  async function changeStage(newStage) {
    setStageMenuOpen(false);
    const oldStage = localDeal.stage;
    const outreach = isOutreachDeal(localDeal);
    const promoting = newStage === "__promote__";

    // An Outreach deal moves on its own stages, dating each milestone, and
    // leaves Outreach by moving into Pipeline (see services/outreachFlow).
    if (outreach || promoting) {
      const patch = promoting ? promotePatch() : outreachStagePatch(localDeal, newStage);
      const { data, error } = await supabase.from("deals").update(patch).eq("id", localDeal.id).select().single();
      if (error) { console.error("Outreach stage change failed:", error.message); return; }
      const updated = { ...dbToDeal(data), photoPath: localDeal.photoPath };
      const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
      const account = trackAccounts?.[0];
      await logActivity({
        userName: account?.name || trackUser || "Unknown",
        userEmail: account?.username || "",
        action: "stage_changed",
        description: promoting
          ? `${dealName} moved from Outreach (${oldStage}) into Pipeline → ${updated.stage}`
          : `${dealName} moved from ${oldStage || "?"} → ${updated.stage}`,
        entityType: "deal",
        entityId: localDeal.id,
        metadata: { old_stage: oldStage, new_stage: updated.stage, deal_name: dealName,
                    flow: promoting ? "outreach_to_pipeline" : "outreach" },
      });
      setLocalDeal(updated);
      onDealUpdated?.(updated);
      if (promoting) refreshDealTiming(updated);
      return;
    }

    // Moving TO a terminal stage → defer to the App-level Kill modal so the
    // user captures reason + narrative (+ competition if Lost). App will
    // handle the write and echo back via onDealUpdated / activeDeal sync.
    if (KILLED_STAGES.includes(newStage) && onOpenKillModal) {
      onOpenKillModal(localDeal, newStage);
      return;
    }

    // Reviving from a terminal stage → clear killed_at so review UIs stop
    // treating the deal as dead.
    const revivePatch = KILLED_STAGES.includes(oldStage) && !KILLED_STAGES.includes(newStage)
      ? { killed_at: null } : {};

    const { data, error } = await supabase
      .from("deals")
      .update({ deal_stage: newStage, ...revivePatch })
      .eq("id", localDeal.id)
      .select()
      .single();
    if (!error) {
      const updated = { ...dbToDeal(data), photoPath: localDeal.photoPath };
      const dealName = parseDealName(updated.sourceNotes) || updated.addresses?.[0] || "Unknown deal";
      const account = trackAccounts?.[0];
      await logActivity({
        userName: account?.name || trackUser || "Unknown",
        userEmail: account?.username || "",
        action: "stage_changed",
        description: `${dealName} moved from ${oldStage || "?"} → ${newStage}`,
        entityType: "deal",
        entityId: localDeal.id,
        metadata: { old_stage: oldStage, new_stage: newStage, deal_name: dealName },
      });
      await autoIncrementTasks(account?.username, "stage_changed");
      setLocalDeal(updated);
      onDealUpdated?.(updated);
      refreshDealTiming(updated);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    const { error } = await supabase.from("deals").delete().eq("id", localDeal.id);
    if (!error) {
      onDealDeleted?.(localDeal.id);
    }
    setDeleting(false);
  }

  async function handlePhotoUpload(file) {
    if (!file || photoUploading) return;
    setPhotoUploading(true);
    try {
      const storagePath = `${localDeal.id}/photo_${Date.now()}_${file.name}`;
      const { error: uploadErr } = await supabase.storage.from("deal-documents").upload(storagePath, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { error: dbErr } = await supabase.from("deals").update({ photo_path: storagePath }).eq("id", localDeal.id).select().single();
      if (dbErr) throw dbErr;
      const url = await getDocumentUrl(storagePath);
      if (url) setPhotoUrl(url);
      const updatedDeal = { ...localDeal, photoPath: storagePath };
      setLocalDeal(updatedDeal);
      onDealUpdated?.(updatedDeal);
    } catch (e) {
      console.error("Photo upload error:", e);
      window.alert(`Photo upload failed: ${e?.message || e}\n\nIf this is a size error, the bucket limit needs to be raised in Supabase.`);
    } finally {
      setPhotoUploading(false);
    }
  }

  // Document upload helper. Wraps uploadDocument in try/catch so failures
  // (size limit, RLS, network, etc.) surface as a visible alert instead of
  // dying silently in the console. Returns the count of successful uploads.
  //
  // Defensively flushes any stale Supabase session at the start of the run
  // — if MSAL or an old preview build left a busted JWT in localStorage,
  // PostgREST treats the request as `role=authenticated` with a NULL uid,
  // which trips RLS policies that expect plain anon writes.
  async function uploadFilesWithFeedback(files) {
    let authNote = "";
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        authNote = `auth=session(${data.session.user?.email || data.session.user?.id || "unknown"})`;
        // Stale session — clear it and continue as anon.
        await supabase.auth.signOut();
        authNote += " → cleared, retrying as anon";
      } else {
        authNote = "auth=anon";
      }
    } catch (e) {
      authNote = `auth=check_failed(${e?.message || e})`;
    }

    const failures = [];
    let successes = 0;
    for (const file of files) {
      try {
        await uploadDocument(localDeal.id, file);
        successes++;
      } catch (err) {
        console.error("Upload failed for", file.name, err);
        const fields = [];
        if (err?.message) fields.push(err.message);
        if (err?.statusCode || err?.status) fields.push(`status=${err.statusCode || err.status}`);
        if (err?.code) fields.push(`code=${err.code}`);
        if (err?.error && err.error !== err.message) fields.push(err.error);
        if (err?.hint) fields.push(`hint: ${err.hint}`);
        if (err?.details) fields.push(`details: ${err.details}`);
        const sizeMb = (file.size / 1_048_576).toFixed(1);
        failures.push(`• ${file.name} (${sizeMb} MB)\n  ${fields.join("\n  ") || String(err)}`);
      }
    }
    if (failures.length > 0) {
      const header = `${failures.length} of ${files.length} file${files.length > 1 ? "s" : ""} failed to upload:`;
      const blob = failures.join("\n\n");
      const hint = /size|large|limit|exceed/i.test(blob)
        ? "\n\nLooks like a size cap — raise the Supabase deal-documents bucket fileSizeLimit."
        : /row-level security|rls/i.test(blob)
          ? "\n\nLooks like an RLS denial. Check the storage.objects or documents table policies in Supabase."
          : "";
      window.alert(`${header}\n\n${blob}\n\n${authNote}${hint}`);
    }
    return successes;
  }

  function handleInlineSave(field, newValue) {
    // Map from DB column names or JS keys to the localDeal key
    const keyMap = {
      thesis:         "thesis",
      open_questions: "openQuestions",
      red_flags:      "redFlags",
      sourceNotes:    "sourceNotes",
    };
    const dealKey = keyMap[field] ?? field;
    const updated = { ...localDeal, [dealKey]: newValue };
    setLocalDeal(updated);
    onDealUpdated?.(updated);
  }

  // Which half of the deal page is showing. The dashboard is the
  // default because it answers the question first - the deal room's
  // facts and chat are where you go once you have a question.
  const [view, setView] = useState("dashboard");

  const editLabelCls = "block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5";

  const dealName = parseDealName(localDeal.sourceNotes);

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-[#242424] text-white' : 'theme-light bg-pp-light text-pp-dark'}`}>
      {/* Comp extraction staging modal — shared between DealCompSet's
          "Extract from Document ▾" picker and the per-document Extract
          button in the Documents section. Renders at the top of DealRoom
          so it overlays everything. */}
      <CompExtractionStaging
        open={!!extractRequest}
        onClose={() => setExtractRequest(null)}
        onSaved={() => { setExtractRequest(null); setCompSetReloadKey(k => k + 1); }}
        dealId={localDeal.id}
        dealName={dealName || (localDeal.addresses?.[0] ?? "")}
        fileName={extractRequest?.fileName}
        file={extractRequest?.file}
        createdBy={trackAccounts?.[0]?.name || null}
        darkMode={darkMode}
      />
      <header className={`border-b backdrop-blur sticky top-0 z-10 ${darkMode ? 'border-[rgba(58,58,58,0.6)] bg-[rgba(36,36,36,0.95)]' : 'border-pp-gray bg-white/95'}`}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <button
            onClick={onBack}
            className={`flex items-center gap-1.5 transition-colors text-sm ${darkMode ? 'text-[#8a8a8a] hover:text-white' : 'text-[#8a8a8a] hover:text-pp-dark'}`}
          >
            ← Pipeline
          </button>
          <div className={`h-4 w-px ${darkMode ? 'bg-[#3a3a3a]' : 'bg-pp-gray'}`} />
          <div className="flex items-center gap-3 min-w-0">
            <PPIcon size={22} />
            <div className="min-w-0">
              <h1 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-pp-dark'}`}>{dealName || (localDeal.addresses?.[0] ?? "")}</h1>
              {dealName && localDeal.addresses?.[0] && (
                <p className={`text-xs truncate ${darkMode ? 'text-[#8a8a8a]' : 'text-[#8a8a8a]'}`}>{localDeal.addresses[0]}</p>
              )}
            </div>
            {(localDeal.addresses?.length ?? 1) > 1 && (
              <span className={`flex-shrink-0 text-xs rounded px-1.5 py-0.5 ${darkMode ? 'bg-[rgba(58,58,58,0.6)] text-[#8a8a8a]' : 'bg-[#d8d8d8] text-[#8a8a8a]'}`}>
                +{localDeal.addresses.length - 1} more
              </span>
            )}
            {(uwVersions.length > 0 || !readOnly) && (
              <UwVersionSelect
                versions={uwVersions}
                selectedId={uwVersionId}
                onSelect={setUwVersionId}
                onUpload={readOnly ? null : () => setUwUploadOpen(true)}
              />
            )}
            {uwUploadOpen && (
              <Suspense fallback={null}>
                <UwUploadDialog
                  dealId={deal.id}
                  versions={uwVersions}
                  userName={trackUser}
                  onClose={() => setUwUploadOpen(false)}
                  onSaved={afterUwUpload}
                />
              </Suspense>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* View on Map — jumps to the Map view and focuses on this deal's
                pin(s). Multi-address deals fit-bounds to all addresses. */}
            {onShowOnMap && (
              <button
                onClick={onShowOnMap}
                style={{ color: '#ffffff' }}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors bg-pp-red hover:bg-pp-red-light font-montserrat"
                title="View this deal on the map"
              >
                <span className="text-sm leading-none">📍</span>
                View on Map
              </button>
            )}
            {/* Quick stage-change dropdown */}
            <div className="relative">
              {readOnly ? (
                <span title={localDeal.stage}>
                  <StageBadge stage={localDeal.stage} />
                </span>
              ) : (
                <button
                  onClick={() => setStageMenuOpen((v) => !v)}
                  className="flex items-center gap-0.5 hover:opacity-80 transition-opacity"
                  title="Change stage"
                >
                  <StageBadge stage={localDeal.stage} />
                  <span className="text-[#8a8a8a] text-xs leading-none ml-0.5">▾</span>
                </button>
              )}
              {!readOnly && stageMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setStageMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 min-w-[130px]">
                    {(isOutreachDeal(localDeal) ? OUTREACH_STAGES : STAGES).map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStage(s)}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#3a3a3a] transition-colors ${
                          s === localDeal.stage ? "text-[#f04f44] font-semibold" : "text-[#b4b4b4]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                    {isOutreachDeal(localDeal) && (
                      <button onClick={() => changeStage("__promote__")}
                        className="w-full text-left px-3 py-1.5 text-xs text-[#7cbb99] hover:bg-[#3a3a3a] border-t border-[#3a3a3a] mt-1 pt-2 whitespace-nowrap">
                        Move to Pipeline ({OUTREACH_PROMOTE_STAGE}) →
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {!readOnly && (
              <>
                <div className={`h-4 w-px ${darkMode ? 'bg-[#3a3a3a]' : 'bg-pp-gray'}`} />

                {/* Edit / Save / Cancel */}
                {editing ? (
                  <>
                    <button
                      onClick={saveEdit}
                      disabled={editSaving}
                      className="text-xs bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {editSaving ? "Saving…" : "Save Changes"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={editSaving}
                      className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${darkMode ? 'text-[#8a8a8a] hover:text-white hover:bg-[#3a3a3a]' : 'text-[#8a8a8a] hover:text-pp-dark hover:bg-[#d8d8d8]'}`}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={startEdit}
                    className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${darkMode ? 'text-[#8a8a8a] hover:text-white hover:bg-[#3a3a3a]' : 'text-[#8a8a8a] hover:text-pp-dark hover:bg-[#d8d8d8]'}`}
                  >
                    Edit
                  </button>
                )}

                {/* Delete */}
                {!editing && !confirmDelete && (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${darkMode ? 'text-[#7a7a7a] hover:text-[#f04f44] hover:bg-[rgba(44,44,44,0.8)]' : 'text-[#8a8a8a] hover:text-[#e93529] hover:bg-[rgba(233,53,41,0.07)]'}`}
                    title="Delete deal"
                  >
                    Delete
                  </button>
                )}
                {confirmDelete && (
                  <div className="flex items-center gap-2 text-xs bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5">
                    <span className="text-[#b4b4b4]">Delete deal?</span>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="text-[#f04f44] hover:text-[#f4827a] font-semibold disabled:opacity-50 transition-colors"
                    >
                      {deleting ? "Deleting…" : "Yes, delete"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="text-[#8a8a8a] hover:text-[#b4b4b4] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* An Outreach deal (broker LOI) carries its LOI above every tab. */}
      {isOutreachDeal(localDeal) && (
        <OutreachPanel
          deal={localDeal}
          readOnly={readOnly}
          onStage={changeStage}
          onUpdated={(u) => { setLocalDeal(u); onDealUpdated?.(u); }}
        />
      )}

      {/* Dashboard / Deal Room toggle. The dashboard needs the full width -
          its tables and the four-way comparison do not fit a half column -
          so it replaces the two-panel layout rather than sitting inside it. */}
      <div className="max-w-7xl mx-auto w-full px-6 pt-4">
        <div className="inline-flex rounded-xl bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] p-1">
          {[["dashboard", "Dashboard"], ["room", "Deal Room"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition ${
                view === k ? "bg-[#3a3a3a] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#f4f4f4]"}`}>
              {label}
            </button>
          ))}
          {/* The IC Memo opens on its own page in a new tab - a link that can be
              shared, and nothing on screen but the memo. It opens on the version
              selected here (no ?v when that is the current one). */}
          <button
            onClick={() => {
              const v = uwVersions.find((x) => x.id === uwVersionId);
              window.open(memoPath(localDeal, allDeals, v && !v.is_current ? v.version : null), "_blank", "noopener");
            }}
            title="Open the IC Memo in a new tab"
            className="ml-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-semibold transition bg-[#e93529] hover:bg-[#f04f44]"
            style={{ color: "#ffffff" }}>
            IC Memo <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {view === "memo" ? (
        <div className="flex-1 w-full min-w-0 min-h-0 overflow-y-auto overflow-x-hidden mt-4">
          <Suspense fallback={<div className="max-w-7xl mx-auto px-6 py-10 text-[#8a8a8a] text-sm">Loading IC Memo…</div>}>
            {uwLoaded ? <IcMemoTab
              deal={localDeal}
              versions={uwVersions}
              versionId={uwVersionId}
              readOnly={readOnly}
              userName={trackUser}
              userEmail={trackAccounts?.[0]?.username || null}
            /> : <div className="max-w-7xl mx-auto px-6 py-10 text-[#8a8a8a] text-sm">Loading IC Memo…</div>}
          </Suspense>
        </div>
      ) : view === "dashboard" ? (
        <div className="flex-1 max-w-7xl mx-auto w-full min-h-0 overflow-y-auto">
          <DealDashboard deal={{
            id: localDeal.id,
            square_footage: localDeal.squareFeet ?? localDeal.square_footage ?? null,
            latitude: localDeal.latitude ?? null,
            longitude: localDeal.longitude ?? null,
            clear_height: localDeal.clearHeight ?? localDeal.clear_height ?? null,
            door_type: localDeal.doorType ?? localDeal.door_type ?? null,
          }} />
        </div>
      ) : (

      /* min-h-0 on the row lets the children scroll independently within the viewport */
      <div className="flex-1 flex max-w-7xl mx-auto w-full px-6 py-6 gap-6 min-h-0">
        {/* Left panel — scrolls independently; height capped to viewport minus header */}
        <aside className="flex-1 min-w-0 flex flex-col gap-4 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 72px)" }}>

          {editing && editForm ? (
            /* ══ EDIT FORM ════════════════════════════════════════════════════ */
            <div className="flex flex-col gap-4">
              {editError && (
                <p className="text-[#f04f44] text-xs bg-[rgba(196,45,34,0.2)] border border-[rgba(196,45,34,0.3)] rounded-lg px-3 py-2">{editError}</p>
              )}

              {/* Deal Name */}
              <div>
                <label className={editLabelCls}>Deal Name</label>
                <input
                  type="text"
                  value={editForm.dealName}
                  onChange={(e) => setEditField("dealName", e.target.value)}
                  placeholder="e.g. Weston Business Park"
                  className={inputCls()}
                />
              </div>

              {/* Addresses */}
              <div>
                <label className={editLabelCls}>Address(es)</label>
                <div className="flex flex-col gap-2">
                  {editForm.addresses.map((addr, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={addr}
                        onChange={(e) => setEditAddr(idx, e.target.value)}
                        className={inputCls() + " flex-1"}
                      />
                      {idx > 0 && (
                        <button type="button" onClick={() => removeEditAddr(idx)}
                          className="text-[#8a8a8a] hover:text-[#f04f44] text-base flex-shrink-0 transition-colors">×</button>
                      )}
                    </div>
                  ))}
                </div>
                {editForm.addresses.length < 10 && (
                  <button type="button" onClick={addEditAddr}
                    className="mt-1.5 text-xs text-[#f04f44] hover:text-[#f04f44] transition-colors">
                    + Add Address
                  </button>
                )}
              </div>

              {/* Stage. Market/MSA is auto-derived from the address list on
                  save (see updateDeal → _tagAddresses in supabase.js), so it
                  no longer needs an editor here — the Fact panel's MSA chips
                  are the read-only surface. */}
              <div>
                <label className={editLabelCls}>Stage</label>
                <select value={editForm.stage} onChange={(e) => setEditField("stage", e.target.value)} className={inputCls()}>
                  {(isOutreachDeal(localDeal) ? OUTREACH_STAGES : STAGES).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Broker — three plain text fields. The full broker CRM was
                  removed in favor of capturing name + company + email
                  directly on the deal; we'll build the CRM back out later
                  when the shape is clearer. */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={editLabelCls}>Broker Name</label>
                  <input value={editForm.brokerName}
                    onChange={(e) => setEditField("brokerName", e.target.value)}
                    placeholder="e.g. Jane Doe"
                    className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>Broker Company</label>
                  <input value={editForm.brokerFirm}
                    onChange={(e) => setEditField("brokerFirm", e.target.value)}
                    placeholder="e.g. CBRE"
                    className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>Broker Email</label>
                  <input type="email" value={editForm.brokerEmail}
                    onChange={(e) => setEditField("brokerEmail", e.target.value)}
                    placeholder="jane.doe@cbre.com"
                    className={inputCls()} />
                </div>
              </div>

              {/* Best & Final — captured on the deal itself so the Review
                  Pipeline funnel can populate its Best & Final tier without
                  waiting for the Kill modal (which only fires on Lost). */}
              <div className="pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-[#f4f4f4] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!editForm.madeBestAndFinal}
                    onChange={(e) => setEditField("madeBestAndFinal", e.target.checked)}
                    className="w-4 h-4 accent-pp-red"
                  />
                  <span className="font-semibold">Made it to Best &amp; Final</span>
                  <span className="text-xs text-[#8a8a8a] font-normal">(implies IC met)</span>
                </label>
              </div>

              {/* Product Type / Year Built / Clear Height — buy-box
                  dimensions. Changing any of these re-grades the deal on
                  save via the auto-tag in updateDeal. */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={editLabelCls}>Product Type</label>
                  <select value={editForm.productType || ""} onChange={(e) => setEditField("productType", e.target.value)} className={inputCls()}>
                    <option value="">— unspecified —</option>
                    {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={editLabelCls}>Year Built</label>
                  <input type="number" value={editForm.yearBuilt}
                    onChange={(e) => setEditField("yearBuilt", e.target.value)}
                    placeholder="e.g. 1997" className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>Clear Height (ft)</label>
                  <input type="number" step="0.5" value={editForm.clearHeight}
                    onChange={(e) => setEditField("clearHeight", e.target.value)}
                    placeholder="e.g. 28" className={inputCls()} />
                </div>
              </div>

              {/* SF / Price / Cap */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={editLabelCls}>SF</label>
                  <input type="number" value={editForm.squareFeet}
                    onChange={(e) => setEditField("squareFeet", e.target.value)}
                    className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>Ask ($)</label>
                  <input type="number" value={editForm.askingPrice}
                    onChange={(e) => setEditField("askingPrice", e.target.value)}
                    placeholder="TBD" className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>Cap (%)</label>
                  <input type="number" step="0.01" value={editForm.capRate}
                    onChange={(e) => setEditField("capRate", e.target.value)}
                    placeholder="TBD" className={inputCls()} />
                </div>
                <div>
                  <label className={editLabelCls}>OPEX ($/SF/yr)</label>
                  <input type="number" step="0.01" value={editForm.opexPsf}
                    onChange={(e) => setEditField("opexPsf", e.target.value)}
                    placeholder="TBD" className={inputCls()} />
                </div>
              </div>

              {/* Call For Offers (CFO) date — broker's bid deadline */}
              <div>
                <label className={editLabelCls}>Call For Offers (CFO) Date</label>
                <input type="date" value={editForm.callForOffersDate || ""}
                  onChange={(e) => setEditField("callForOffersDate", e.target.value)}
                  className={inputCls()} />
              </div>

              {/* Marketed toggle — On-Market vs Off-Market */}
              <div>
                <label className={editLabelCls}>Sourcing</label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!editForm.marketed}
                    onChange={(e) => setEditField("marketed", e.target.checked)}
                    className="accent-pp-red w-4 h-4"
                  />
                  <span className="text-sm" style={{ color: editForm.marketed ? "#e93529" : "#8a8a8a" }}>
                    {editForm.marketed ? "On-Market (marketed)" : "Off-Market"}
                  </span>
                </label>
              </div>

              {/* UW Metrics in edit form */}
              <div>
                <label className={editLabelCls}>Underwriting Metrics</label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[
                    { field: "underwrittenPrice", label: "PPUSA Price ($)", placeholder: "Our price" },
                    { field: "leveredIrr",        label: "IRR (%)",         placeholder: "e.g. 18.5" },
                    { field: "equityMultiple",    label: "Eq. Multiple (x)",placeholder: "e.g. 2.1"  },
                  ].map(({ field, label, placeholder }) => (
                    <div key={field}>
                      <label className="block text-xs text-[#8a8a8a] mb-1">{label}</label>
                      <input type="number" step="0.01" value={editForm[field]}
                        onChange={(e) => setEditField(field, e.target.value)}
                        placeholder={placeholder} className={inputCls()} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { field: "trendedYoc",   label: "Trended YoC (%)",   placeholder: "e.g. 7.5" },
                    { field: "untrendedYoc", label: "Untrended YoC (%)", placeholder: "e.g. 6.8" },
                    { field: "averageCoc",   label: "Avg CoC (%)",        placeholder: "e.g. 8.0" },
                  ].map(({ field, label, placeholder }) => (
                    <div key={field}>
                      <label className="block text-xs text-[#8a8a8a] mb-1">{label}</label>
                      <input type="number" step="0.01" value={editForm[field]}
                        onChange={(e) => setEditField(field, e.target.value)}
                        placeholder={placeholder} className={inputCls()} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Source Notes */}
              <div>
                <label className={editLabelCls}>OM / Source Notes</label>
                <div className="border-l-2 border-l-amber-500 pl-3">
                  <textarea rows={3} value={editForm.sourceNotes}
                    onChange={(e) => setEditField("sourceNotes", e.target.value)}
                    className={inputCls() + " resize-none"} />
                </div>
              </div>

              {/* Thesis / Open Questions / Red Flags */}
              {[
                { field: "thesis",        label: "Investment Thesis",  rows: 3 },
                { field: "openQuestions", label: "Open Questions",     rows: 3 },
                { field: "redFlags",      label: "Red Flags",          rows: 3 },
              ].map(({ field, label, rows }) => (
                <div key={field}>
                  <label className={editLabelCls}>{label}</label>
                  <textarea rows={rows} value={editForm[field]}
                    onChange={(e) => setEditField(field, e.target.value)}
                    className={inputCls() + " resize-none"} />
                </div>
              ))}

              {/* Save / Cancel */}
              <div className="flex gap-3 pt-1 pb-2">
                <button onClick={saveEdit} disabled={editSaving}
                  className="bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-50 text-[#333333] font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
                  {editSaving ? "Saving…" : "Save Changes"}
                </button>
                <button onClick={cancelEdit} disabled={editSaving}
                  className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
                  Cancel
                </button>
              </div>
            </div>

          ) : (
            /* ══ READ-ONLY VIEW ═══════════════════════════════════════════════ */
            <>
              {/* The top row: a Street View you can walk around, with the
                  property's own photo beside it. Replaced a static thumbnail,
                  which answered one question and stopped - a panorama answers
                  the ones people actually ask of a deal photo: what is next
                  door, how does the loading face the street, what is the
                  approach like. */}
              <PropertyMedia
                lat={localDeal.latitude}
                lng={localDeal.longitude}
                photoUrl={photoUrl}
                address={Array.isArray(localDeal.addresses) ? localDeal.addresses[0] : dealName}
                readOnly={readOnly}
                onChangePhoto={() => dealPhotoInputRef.current?.click()}
              />
              {!readOnly && (
                <input ref={dealPhotoInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])} />
              )}

              <FactSection title="Overview">
                {(localDeal.addresses?.length ?? 0) <= 1 ? (
                  <FactRow label="Address" value={localDeal.addresses?.[0] ?? ""} />
                ) : (
                  <div className="py-1 border-b border-[rgba(58,58,58,0.3)]">
                    <span className="text-xs text-[#8a8a8a]">Addresses ({localDeal.addresses.length})</span>
                    <div className="mt-1 flex flex-col gap-0.5">
                      {localDeal.addresses.map((addr, i) => (
                        <p key={i} className="text-sm font-medium text-[#f4f4f4]">{addr}</p>
                      ))}
                    </div>
                  </div>
                )}
                <FactRow label="Market" value={<MarketChips msas={localDeal.msas} fallback={localDeal.market} />} />
                {localDeal.productType && (
                  <FactRow label="Product" value={
                    (PRODUCT_TYPES.find(t => t.value === localDeal.productType)?.label) || localDeal.productType
                  } />
                )}
                {localDeal.yearBuilt && (
                  <FactRow label="Year Built" value={localDeal.yearBuilt} />
                )}
                {localDeal.clearHeight && (
                  <FactRow label="Clear Height" value={`${localDeal.clearHeight}'`} />
                )}
                {(localDeal.brokerName || localDeal.brokerFirm || localDeal.brokerEmail) && (
                  <FactRow label="Broker" value={
                    <span className="text-sm font-medium text-[#f4f4f4] flex flex-col items-end">
                      {localDeal.brokerName && <span>{localDeal.brokerName}</span>}
                      {localDeal.brokerFirm && <span className="text-xs text-[#8a8a8a]">{localDeal.brokerFirm}</span>}
                      {localDeal.brokerEmail && <span className="text-xs text-[#8a8a8a]">{localDeal.brokerEmail}</span>}
                    </span>
                  } />
                )}
                {localDeal.madeBestAndFinal && (
                  <FactRow label="Best & Final" value={
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-pp-red/15 text-pp-red border border-pp-red/40">
                      ✓ Made B&amp;F
                    </span>
                  } />
                )}
                {localDeal.timeToLoiDays != null && (
                  <FactRow label="Time to LOI" value={`${localDeal.timeToLoiDays} d`} />
                )}
                {localDeal.timeInUwDays != null && (
                  <FactRow label="Time in UW" value={`${localDeal.timeInUwDays} d`} />
                )}
                <FactRow label="In Buy Box?" value={
                  <BuyBoxOverride
                    verdict={localDeal.buyBoxVerdict}
                    source={localDeal.buyBoxSource}
                    reason={localDeal.buyBoxReason}
                    saving={buyBoxSaving}
                    onOverride={handleBuyBoxOverride}
                  />
                } />
                <FactRow label="Size" value={fmt.sf(localDeal.squareFeet)} />
                <FactRow label="Asking Price" value={fmt.currency(localDeal.askingPrice)} />
                {/* Which underwriting these rows are showing - chosen in the
                    header, which drives every tab. Said here only when it is
                    not the current one, so a superseded figure is never read
                    as the live underwriting. */}
                {shownUw._label && !shownUw._isCurrent && (
                  <p className="text-[11px] text-[#e9a23b] py-1.5">
                    Showing {shownUw._label} - not the current underwriting
                  </p>
                )}
                <FactRow label="PPUSA Price"   value={fmt.currencyOrDash(shownUw.underwrittenPrice)} accent />
                <FactRow label="Levered IRR"   value={fmt.pct(shownUw.leveredIrr)} />
                <FactRow label="Equity Multiple" value={fmt.multiple(shownUw.equityMultiple)} />
                <FactRow label="Trended YoC"   value={fmt.pct(shownUw.trendedYoc)} />
                <FactRow label="Untrended YoC" value={fmt.pct(shownUw.untrendedYoc)} />
                <FactRow label="Avg CoC"       value={fmt.pct(shownUw.averageCoc)} />
                <FactRow label="Going-In Cap" value={fmt.cap(shownUw.capRate)} accent />
                <FactRow label="OPEX $/SF" value={shownUw.opexPsf != null ? `$${Number(shownUw.opexPsf).toFixed(2)}` : "TBD"} />
                {/* The assumptions behind the selected version. Two versions
                    whose returns differ tell you nothing on their own; hold,
                    exit cap and leverage are usually the whole reason. */}
                {shownUw._v && (
                  <>
                    <FactRow label="Exit Cap" value={fmt.cap(shownUw._v.exit_cap_rate)} />
                    <FactRow label="Hold" value={shownUw._v.sale_month ? `${Math.round(shownUw._v.sale_month / 12)} yrs (${shownUw._v.sale_month} mo)` : "—"} />
                    <FactRow label="LTV" value={fmt.pct(shownUw._v.ltv)} />
                  </>
                )}
                {localDeal.callForOffersDate && (
                  <FactRow label="CFO Date" value={fmtCfoDate(localDeal.callForOffersDate)} accent />
                )}
                <FactRow label="Sourcing" value={localDeal.marketed ? "On-Market" : "Off-Market"} />
                <FactRow label="Stage" value={<StageBadge stage={localDeal.stage} />} />
                {localDeal.createdAt && (
                  <FactRow label="Date Added" value={fmtDateAdded(localDeal.createdAt)} />
                )}
                <a href={googleMapsUrl(localDeal.addresses)} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-[#f04f44] hover:text-[#f04f44] transition-colors">
                  View on Google Maps →
                </a>
                <NearestInfrastructure deal={localDeal} />
              </FactSection>

              {KILLED_STAGES.includes(localDeal.stage) && (
                <KillDetailSection deal={localDeal} onRegrade={() => onOpenKillModal?.(localDeal, localDeal.stage)} />
              )}

              <EditableSourceNotes
                value={localDeal.sourceNotes}
                dealId={localDeal.id}
                onSave={handleInlineSave}
                readOnly={readOnly}
              />

              {/* What people decided about this deal. Sits above the thesis
                  because it is the thing a reader needs first: it says which
                  figures were argued over and settled, and which nobody has
                  looked at. */}
              <DealNotes
                notes={dealNotes}
                dealId={localDeal.id}
                readOnly={readOnly}
                onAdded={(n) => setDealNotes((prev) => [n, ...prev])}
              />

              <EditableTextSection
                title="Investment Thesis"
                field="thesis"
                value={localDeal.thesis}
                dealId={localDeal.id}
                onSave={handleInlineSave}
                readOnly={readOnly}
              />

              <EditableTextSection
                title="Open Questions"
                field="open_questions"
                value={localDeal.openQuestions}
                dealId={localDeal.id}
                onSave={handleInlineSave}
                readOnly={readOnly}
              />

              <EditableTextSection
                title="Red Flags"
                field="red_flags"
                value={localDeal.redFlags}
                dealId={localDeal.id}
                onSave={handleInlineSave}
                accent="red"
                readOnly={readOnly}
              />

              {/* OUTREACH HISTORY (only if entries exist) */}
              {dealOutreach.length > 0 && (
                <div className="bg-[rgba(44,44,44,0.5)] border border-[#3a3a3a] rounded-xl p-5">
                  <h3 className="text-[#e93529] font-bold text-sm tracking-wider mb-3">OUTREACH HISTORY</h3>
                  <div className="space-y-2">
                    {dealOutreach.map(o => (
                      <div key={o.id} className="bg-[#3a3a3a] rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-medium">{new Date(o.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'Sent' ? 'bg-[#c42d22]' : o.status === 'Responded' ? 'bg-[#44906a]' : o.status === 'Passed' ? 'bg-[#c42d22]' : 'bg-[#4a4a4a]'} text-white`}>{o.status}</span>
                        </div>
                        <p className="text-[#b4b4b4]">${Number(o.loi_psf || 0).toLocaleString()}/SF — ${Number(o.loi_total || 0).toLocaleString()} total</p>
                        <p className="text-[#8a8a8a] text-xs">Sent by {o.sent_by}</p>
                        {o.response && <p className="text-[#b4b4b4] text-xs mt-1 italic">Response: {o.response}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* DOCUMENTS SECTION — drag-and-drop + click-to-upload.
                  Hidden entirely for guests if there are no documents to display;
                  guests with docs to view get a read-only file list. */}
              {(!readOnly || documents.length > 0) && (
                <div
                  className="bg-[rgba(44,44,44,0.5)] border border-[#3a3a3a] rounded-xl p-4"
                  onDragOver={readOnly ? undefined : e => { e.preventDefault(); e.currentTarget.style.borderColor = "#e93529"; e.currentTarget.style.backgroundColor = "rgba(233,53,41,0.05)"; }}
                  onDragLeave={readOnly ? undefined : e => { e.currentTarget.style.borderColor = ""; e.currentTarget.style.backgroundColor = ""; }}
                  onDrop={readOnly ? undefined : async e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.backgroundColor = "";
                    const files = Array.from(e.dataTransfer.files).filter(f => /\.(pdf|docx?|xlsx?|xls|csv|txt)$/i.test(f.name));
                    if (files.length === 0) return;
                    await uploadFilesWithFeedback(files);
                    const updated = await loadDocuments(localDeal.id);
                    setDocuments(updated);
                    if (pdfImageCache?.current) pdfImageCache.current = null;
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[#e93529] font-bold text-sm tracking-wider">DOCUMENTS</h3>
                    {!readOnly && (
                      <button
                        className="text-xs text-[#f04f44] hover:text-[#f04f44] transition-colors"
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.multiple = true;
                          input.accept = ".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt";
                          input.onchange = async (e) => {
                            const files = Array.from(e.target.files);
                            await uploadFilesWithFeedback(files);
                            const updated = await loadDocuments(localDeal.id);
                            setDocuments(updated);
                            if (pdfImageCache?.current) pdfImageCache.current = null;
                          };
                          input.click();
                        }}
                      >+ Upload</button>
                    )}
                  </div>
                  {documents.length === 0 ? (
                    <p className="text-[#8a8a8a] text-xs text-center py-4">Drop files here or click + Upload</p>
                  ) : (
                    <div className="space-y-1">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center gap-1.5 py-1.5 group"
                        >
                          <span className="text-sm flex-shrink-0">
                            {doc.file_name?.endsWith(".pdf") ? "📄" :
                             doc.file_name?.match(/\.xlsx?$/i) ? "📊" :
                             doc.file_name?.match(/\.docx?$/i) ? "📝" : "📎"}
                          </span>
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={async () => {
                            try {
                              const url = await getDocumentUrl(doc.storage_path);
                              window.open(url, "_blank");
                            } catch (e) {
                              console.error("Failed to get document URL:", e);
                            }
                          }}>
                            <span className="text-sm text-[#f04f44] hover:text-[#f04f44] truncate block">{doc.file_name}</span>
                            {doc.created_at && (
                              <span className="text-[10px] text-[#8a8a8a]">
                                {new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          {!readOnly && (
                            <button
                              onClick={(e) => { e.stopPropagation(); triggerExtractFromDoc(doc); }}
                              disabled={extractingDocId === doc.id}
                              className="opacity-0 group-hover:opacity-100 text-[#f04f44] hover:text-[#f04f44] transition-all px-2 text-[10px] font-semibold flex-shrink-0 disabled:opacity-50"
                              title="Run AI extraction (comps + rent roll) and stage results for review"
                            >
                              {extractingDocId === doc.id ? "..." : "📊 Extract"}
                            </button>
                          )}
                          {!readOnly && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
                                try {
                                  await deleteDocument(doc);
                                  const updated = await loadDocuments(localDeal.id);
                                  setDocuments(updated);
                                } catch (err) {
                                  console.error("Delete doc error:", err);
                                }
                              }}
                              className="opacity-0 group-hover:opacity-100 text-[#8a8a8a] hover:text-[#f04f44] transition-all p-1 rounded flex-shrink-0"
                              title="Delete document"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <DealCompSet
                darkMode={darkMode}
                dealId={localDeal.id}
                dealName={dealName || (localDeal.addresses?.[0] ?? "")}
                dealAddresses={localDeal.addresses || []}
                dealLat={localDeal.latitude || null}
                dealLng={localDeal.longitude || null}
                documents={documents}
                currentUser={trackAccounts?.[0] ? { name: trackAccounts[0].name, email: trackAccounts[0].username } : null}
                readOnly={readOnly}
                onExtractFromDoc={triggerExtractFromDoc}
                reloadSignal={compSetReloadKey}
              />
            </>
          )}
        </aside>

      </div>
      )}
    </div>
  );
}

function FactSection({ title, accent, children }) {
  const titleColor = accent === "red" ? "text-[#f04f44]" : "text-[#f04f44]";
  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl p-4">
      <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${titleColor}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

// Renders the deal's MSAs as small red-outlined chips. Multi-metro deals show
// one chip per MSA; empty msas[] falls back to the joined-string market so
// legacy rows still render something.
function MarketChips({ msas, fallback }) {
  const list = Array.isArray(msas) ? msas.filter(Boolean) : [];
  if (list.length === 0) return <span>{fallback || "—"}</span>;
  return (
    <span className="inline-flex flex-wrap justify-end gap-1">
      {list.map(m => (
        <span key={m}
          className="inline-flex items-center rounded-full border border-pp-red/40 bg-pp-red/10 text-pp-red text-[11px] font-semibold px-2 py-0.5">
          {m}
        </span>
      ))}
    </span>
  );
}

function FactRow({ label, value, accent }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-1 border-b border-[rgba(58,58,58,0.3)] last:border-0">
      <span className="text-xs text-[#8a8a8a] flex-shrink-0">{label}</span>
      <span className={`text-sm font-medium text-right ${accent ? "text-[#f04f44]" : "text-[#f4f4f4]"}`}>
        {value}
      </span>
    </div>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  const isError = msg.role === "error";

  if (isError) {
    return (
      <div className="flex justify-start">
        <div className="max-w-prose text-sm px-4 py-2.5 rounded-2xl rounded-bl-sm bg-[rgba(196,45,34,0.4)] border border-[rgba(196,45,34,0.5)] text-[#f4827a] leading-relaxed">
          <span className="font-semibold">Error: </span>{msg.text}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mr-2 mt-0.5 bg-pp-red" style={{ padding: 3 }}>
          <img src={iconWhite} alt="PP" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
        </div>
      )}
      <div
        className={`max-w-prose text-sm px-4 py-2.5 rounded-2xl leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-pp-red font-medium rounded-br-sm"
            : "rounded-bl-sm"
        }`}
        style={isUser
          ? { color: '#ffffff' }
          : { backgroundColor: '#f4f4f4', color: '#4a4a4a' }
        }
      >
        {msg.text}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mr-2 mt-0.5 bg-pp-red" style={{ padding: 3 }}>
        <img src={iconWhite} alt="PP" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
      </div>
      <div className="px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5" style={{ backgroundColor: '#f4f4f4' }}>
        <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

// Inline-editable textarea section with 1-second debounced auto-save.
// field must match the Supabase column name (e.g. "thesis", "open_questions").
function EditableTextSection({ title, field, value, dealId, onSave, accent, readOnly = false }) {
  const [text, setText]     = useState(value ?? "");
  const [status, setStatus] = useState(null); // null | "saving" | "saved"
  const timerRef = useRef(null);

  // Keep local text in sync if parent updates the value (e.g. after full edit)
  useEffect(() => { setText(value ?? ""); }, [value]);

  function handleChange(e) {
    const newVal = e.target.value;
    setText(newVal);
    setStatus(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus("saving");
      const { error } = await supabase
        .from("deals")
        .update({ [field]: newVal })
        .eq("id", dealId);
      if (!error) {
        onSave?.(field, newVal);
        setStatus("saved");
        setTimeout(() => setStatus(null), 2000);
      } else {
        setStatus(null);
      }
    }, 1000);
  }

  const titleColor = accent === "red" ? "text-[#f04f44]" : "text-[#f04f44]";
  const textareaRef = useRef(null);

  // Auto-expand textarea to fit content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [text]);

  if (readOnly) {
    return (
      <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl p-4">
        <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${titleColor}`}>{title}</h3>
        {text?.trim() ? (
          <p className="text-sm text-[#b4b4b4] leading-relaxed whitespace-pre-wrap">{text}</p>
        ) : (
          <p className="text-sm italic text-[#8a8a8a]">—</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-xs font-bold uppercase tracking-wider ${titleColor}`}>{title}</h3>
        {status === "saving" && <span className="text-xs text-[#8a8a8a]">Saving…</span>}
        {status === "saved"  && <span className="text-xs text-[#4f9d76]">Saved ✓</span>}
      </div>
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        rows={4}
        placeholder={`Add ${title.toLowerCase()}…`}
        className="w-full bg-transparent border border-transparent focus:border-[#4a4a4a] focus:bg-[rgba(44,44,44,0.6)] rounded-lg px-2 py-1.5 text-sm text-[#b4b4b4] leading-relaxed resize-none focus:outline-none transition-colors placeholder-[#5a5a5a] min-h-[150px]"
      />
    </div>
  );
}

// Same as EditableTextSection but with the amber left-border treatment for OM/Source Notes.
function EditableSourceNotes({ value, dealId, onSave, readOnly = false }) {
  const [text, setText]     = useState(value ?? "");
  const [status, setStatus] = useState(null);
  const timerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { setText(value ?? ""); }, [value]);

  // Auto-expand textarea to fit content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [text]);

  function handleChange(e) {
    const newVal = e.target.value;
    setText(newVal);
    setStatus(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus("saving");
      const { error } = await supabase
        .from("deals")
        .update({ source_notes: newVal })
        .eq("id", dealId);
      if (!error) {
        onSave?.("sourceNotes", newVal);
        setStatus("saved");
        setTimeout(() => setStatus(null), 2000);
      } else {
        setStatus(null);
      }
    }, 1000);
  }

  if (readOnly) {
    return (
      <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] border-l-2 border-l-amber-500 rounded-xl p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#f04f44] mb-3">OM / Source Notes</h3>
        {text?.trim() ? (
          <p className="text-sm text-[#b4b4b4] leading-relaxed whitespace-pre-wrap">{text}</p>
        ) : (
          <p className="text-sm italic text-[#8a8a8a]">—</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] border-l-2 border-l-amber-500 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#f04f44]">OM / Source Notes</h3>
        {status === "saving" && <span className="text-xs text-[#8a8a8a]">Saving…</span>}
        {status === "saved"  && <span className="text-xs text-[#4f9d76]">Saved ✓</span>}
      </div>
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        rows={6}
        placeholder="Add source notes…"
        className="w-full bg-transparent border border-transparent focus:border-[#4a4a4a] focus:bg-[rgba(44,44,44,0.6)] rounded-lg px-2 py-1.5 text-sm text-[#b4b4b4] leading-relaxed resize-none focus:outline-none transition-colors placeholder-[#5a5a5a] min-h-[200px]"
      />
    </div>
  );
}

export default DealRoom;
export { buildOMExtractionPrompt, fmtFileSize, fileIcon, UploadZone, buildSystemPrompt, buildDocumentsSection, googleMapsUrl };

// The underwriting version switcher. A pill per version, the current one marked,
// because "which underwriting is this" is the first question anyone asks of a
// number on this page and the answer used to be unanswerable - there was only
// ever one, whatever it had been before.
// Each kind reads differently and should look different: a decision is settled,
// a question is not, and guidance came from outside the firm.
const NOTE_KINDS = {
  decision:   { label: "Decision",   fg: "#f04f44", bg: "rgba(233,53,41,0.12)",  bd: "rgba(233,53,41,0.3)" },
  correction: { label: "Correction", fg: "#c8922f", bg: "rgba(200,146,47,0.12)", bd: "rgba(200,146,47,0.3)" },
  guidance:   { label: "Guidance",   fg: "#4a9d7f", bg: "rgba(74,157,127,0.12)", bd: "rgba(74,157,127,0.3)" },
  question:   { label: "Open",       fg: "#7a9fd4", bg: "rgba(122,159,212,0.12)",bd: "rgba(122,159,212,0.3)" },
  context:    { label: "Context",    fg: "#b4b4b4", bg: "rgba(138,138,138,0.12)",bd: "rgba(138,138,138,0.25)" },
};

function DealNotes({ notes, dealId, readOnly, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("decision");
  const [body, setBody] = useState("");
  const [why, setWhy] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const made = await saveDealNote(dealId, {
        kind,
        body: body.trim(),
        rationale: why.trim() || null,
        author: "Daniel Manzi",
        decided_on: new Date().toISOString().slice(0, 10),
        heard_via: "deal page",
      });
      onAdded?.(made);
      setBody(""); setWhy(""); setAdding(false);
    } catch (e) {
      console.warn("saveDealNote:", e.message);
    } finally {
      setSaving(false);
    }
  }

  // An empty section with no way to add anything is just noise, so a read-only
  // deal with no notes renders nothing at all.
  if (!notes?.length && (readOnly || !adding)) {
    if (readOnly) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#f4f4f4]">Decisions &amp; Context</h3>
          <button onClick={() => setAdding(true)}
            className="text-xs text-[#8a8a8a] hover:text-[#f04f44] transition-colors">
            + Record one
          </button>
        </div>
        <p className="text-xs text-[#6a6a6a] italic">
          Nothing recorded. This is where a call about the deal lives — a lease type
          settled, guidance from a broker, a figure corrected — so the next person
          can tell it from a number nobody has checked.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#f4f4f4]">
          Decisions &amp; Context
          {notes?.length ? <span className="ml-2 text-xs text-[#8a8a8a]">{notes.length}</span> : null}
        </h3>
        {!readOnly && !adding && (
          <button onClick={() => setAdding(true)}
            className="text-xs text-[#8a8a8a] hover:text-[#f04f44] transition-colors">
            + Record one
          </button>
        )}
      </div>

      {adding && (
        <div className="mb-3 p-3 rounded-lg bg-[rgba(44,44,44,0.6)] border border-[#3a3a3a]">
          <div className="flex gap-1.5 mb-2 flex-wrap">
            {Object.entries(NOTE_KINDS).map(([k, s]) => (
              <button key={k} onClick={() => setKind(k)}
                className="text-[10px] font-bold px-2 py-0.5 rounded-full transition-all"
                style={kind === k
                  ? { backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.bd}` }
                  : { backgroundColor: "transparent", color: "#6a6a6a", border: "1px solid #3a3a3a" }}>
                {s.label}
              </button>
            ))}
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2}
            placeholder="What was decided"
            className="w-full text-xs bg-[#1f1f1f] text-[#f4f4f4] border border-[#3a3a3a] rounded p-2 mb-2 focus:outline-none focus:border-[#f04f44]" />
          <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2}
            placeholder="Why — the reasoning, so it can be argued with later"
            className="w-full text-xs bg-[#1f1f1f] text-[#b4b4b4] border border-[#3a3a3a] rounded p-2 mb-2 focus:outline-none focus:border-[#f04f44]" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setAdding(false); setBody(""); setWhy(""); }}
              className="text-xs text-[#8a8a8a] hover:text-[#f4f4f4] px-2 py-1">Cancel</button>
            <button onClick={save} disabled={!body.trim() || saving}
              className="text-xs bg-[#e93529] hover:bg-[#f04f44] disabled:opacity-40 text-white px-3 py-1 rounded transition-colors">
              {saving ? "Saving…" : "Record"}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(notes || []).map((n) => {
          const s = NOTE_KINDS[n.kind] || NOTE_KINDS.context;
          const when = n.decided_on || (n.created_at || "").slice(0, 10);
          return (
            <div key={n.id}
              className="p-3 rounded-lg bg-[rgba(44,44,44,0.5)] border border-[rgba(58,58,58,0.6)]"
              style={n.superseded_by ? { opacity: 0.5 } : undefined}>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.bd}` }}>
                  {s.label}
                </span>
                {n.author && <span className="text-[10px] text-[#b4b4b4]">{n.author}</span>}
                {when && <span className="text-[10px] text-[#6a6a6a]">{when}</span>}
                {n.applies_to && (
                  <span className="text-[10px] text-[#6a6a6a] font-mono">{n.applies_to}</span>
                )}
                {n.superseded_by && (
                  <span className="text-[9px] font-bold text-[#8a8a8a]">SUPERSEDED</span>
                )}
              </div>
              <p className="text-xs text-[#f4f4f4] leading-relaxed">{n.body}</p>
              {n.rationale && (
                <p className="text-xs text-[#8a8a8a] leading-relaxed mt-1.5 pl-2 border-l-2 border-[#3a3a3a]">
                  {n.rationale}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The underwriting version the whole deal page shows. Defaults to the current
// version; picking another changes the Deal Room facts and the IC Memo, and
// says so - an older version is marked rather than passed off as live.
export function UwVersionSelect({ versions, selectedId, onSelect, onUpload }) {
  const [open, setOpen] = useState(false);
  const sel = versions.find((v) => v.id === selectedId) || versions[versions.length - 1];
  const name = (v) => v.label || `V${v.version}`;
  const date = (v) => (v.created_at ? new Date(v.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        title="Underwriting version shown on every tab"
        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors ${
          sel?.is_current ? "border-[#3a3a3a] text-[#f4f4f4] hover:border-[#6b6b6b]" : "border-[#e9a23b]/60 text-[#e9a23b]"}`}
      >
        <span className="text-[10px] uppercase tracking-wide text-[#8a8a8a] font-semibold">UW</span>
        {sel ? name(sel) : "No UW yet"}
        {sel?.is_current && <span className="text-[9px] font-semibold opacity-70">current</span>}
        <span className="text-[#8a8a8a] leading-none">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 min-w-[200px]">
            {[...versions].reverse().map((v) => (
              <button
                key={v.id}
                onClick={() => { onSelect(v.id); setOpen(false); }}
                title={v.notes || undefined}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#3a3a3a] transition-colors flex items-center justify-between gap-3 ${
                  v.id === sel?.id ? "text-[#f04f44] font-semibold" : "text-[#b4b4b4]"}`}
              >
                <span>{name(v)}{v.is_current && <span className="ml-1.5 text-[9px] opacity-70">current</span>}</span>
                <span className="text-[10px] text-[#6b6b6b]">{date(v)}</span>
              </button>
            ))}
            {onUpload && (
              <button
                onClick={() => { setOpen(false); onUpload(); }}
                className={`w-full text-left px-3 py-1.5 text-xs text-[#f04f44] hover:bg-[#3a3a3a] transition-colors ${versions.length ? "border-t border-[#3a3a3a] mt-1 pt-2" : ""}`}
              >
                + Upload a new UW version…
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

